import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet,
  getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost,
  listBespokeTemplatesBespokeTemplatesGet,
  listBespokeTemplateRevisionsBespokeTemplatesRevisionsGet,
  createBespokeTemplateBespokeTemplatesPost,
  updateBespokeTemplateBespokeTemplatesTemplateIdPatch,
  deleteBespokeTemplateBespokeTemplatesTemplateIdDelete,
  importBespokeTemplatesBundleBespokeTemplatesImportBundlePost,
  bespokeTemplatesBulkRenderStatusBespokeTemplatesBulkRenderStatusPost,
} from '../services/api/sdk.gen';
import type {
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
  SectionOrderItem,
  VersionedQuestionSchema,
  BespokeTemplateResponse,
  BespokeTemplateRevision,
  BespokeTemplateCreate,
  BespokeTemplateUpdate,
} from '../services/api/types.gen';
import type { VisitTypeId } from '../services/api/models/VisitTypeId';
import type { RogerDocFormType } from '../services/api/models/RogerDocFormType';
import { useToast } from '../hooks/useToastContext';
import { formatDateForDisplay } from '../utils/dateUtils';
import { fetchRogerEmrFormSchemaVersions } from './formSchema/fetchRogerEmrFormSchemaVersions';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Label,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@roger/ui-components';
import { SectionEditorPanel } from './formSchema/TemplateVibecoder';
import { FormSchemaSectionNav } from './formSchema/FormSchemaSectionNav';
import { SectionVersionsDialog } from './formSchema/SectionVersionsDialog';
import { ValidationResultsDialog } from './formSchema/ValidationResultsDialog';
import { sortFormTypeCatalogByFamily } from './formSchema/formTypeGrouping';
import { pickInitialVersion } from './formSchema/pickInitialVersion';
import {
  isSectionEditorDraftDirty,
  shouldAdoptActiveTemplateDraft,
  type SectionEditorDraft,
  type SectionEditorDraftSeed,
} from '../store/slices/formSchemaEditorSlice';
import { formatSectionDropdownLabel } from './formSchema/formatSectionDropdownLabel';
import {
  Download,
  Loader2,
  MoreHorizontal,
  Pause,
  Play,
  ClipboardCheck,
  ShieldX,
  SquareStack,
  Upload,
} from 'lucide-react';
import type {
  FormTree,
  FormTreeQuestion,
  SectionLayoutState,
  VibecoderDraft,
} from './formSchema/formSchemaTypes';
import {
  acknowledgeRemotePush,
  applyRemotePushDelta,
  bespokeApiErrorMessage,
  bespokeTemplateMatchesPin,
  bespokeTemplateRevisionsFingerprint,
  clearRemotePush,
  defaultBlankSectionLayoutName,
  demotedPrimarySiblingIdsForPromotion,
  diffRevisionsForRemotePush,
  enabledTemplatesBySectionForPin,
  filterBespokeTemplatesForPin,
  formatRemotePushTimeLabel,
  getSectionLayoutState,
  isEffective,
  isEligibleForSectionPortCopy,
  isPersistedInactiveLayoutDraft,
  latestUnacknowledgedRemotePush,
  layoutGenerationKey,
  orderedFormTreeSectionIds,
  pinFromFormSchemaVersion,
  pinsFromFormSchemaVersions,
  planRevisionsPollFingerprintRefresh,
  removeBespokeTemplateLocal,
  requireSavedBespokeTemplate,
  schemaVersionLabel,
  sectionEditorDraftKey,
  unacknowledgedRemotePush,
  upsertBespokeTemplatesLocal,
  validateLayoutJsonForSection,
  type FormSchemaVersionPin,
  type RemotePushByTemplateId,
  type RemotePushRevisionSnapshot,
} from './formSchema/formSchemaUtils';
import { QuestionsDialog } from './formSchema/QuestionsDialog';
import { FormBulkMatchingLayoutCopyDialog } from './formSchema/FormBulkMatchingLayoutCopyDialog';
import { SectionLayoutPortDialog } from './formSchema/blockCopy/SectionLayoutPortDialog';
import {
  formatMatchingCopyAttribution,
  useMatchingLayoutCopyDialog,
} from './formSchema/useMatchingLayoutCopyDialog';
import type { LayoutNode } from '../components/oasis/bespoke/layout/types';
import type { Question } from '../components/oasis/types';
import { SelectableNestedTable } from './formSchema/SelectableNestedTable';
import {
  buildBespokeExportFilename,
  buildBespokeTemplatesExportBundleFromChosenTemplates,
  collectFormSchemaVersionPinsForFormTypes,
  downloadJsonFile,
  filterBespokeTemplatesForExport,
  type BespokeTemplateExportRow,
} from './formSchema/bespokeTemplatesExport';
import {
  bespokeRowsByLeafKey,
  buildImportNestedGroups,
  collectLeafKeysFromGroups,
  exportRowsToImportUpserts,
  importNestedGroupsToSelectableNestedRows,
  parseBespokeImportBundleJson,
  resolveLegacyImportRowPins,
  type ImportFormTypeGroup,
} from './formSchema/bespokeTemplatesImport';

const DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT =
  'Create a clean, clinician-friendly Roger Layout Schema for this form section. Include every question in the section as an answerable field, using compact grouping and tables where they make the form easier to scan.';

const FORM_SCHEMA_QUERY_PARAMS = {
  formType: 'formType',
  schemaName: 'schemaName',
  schemaVersion: 'schemaVersion',
  section: 'section',
  /** Bespoke template id when editing a non-active (standby) layout version. */
  layoutTemplate: 'layoutTemplate',
} as const;

/** Omit from URL when the edited row is the section's active Live/Paused template. */
function layoutTemplateQueryValue(
  template: BespokeTemplateResponse | null | undefined,
  active: BespokeTemplateResponse | null | undefined
): string | null {
  if (!template) return null;
  if (active?.id === template.id) return null;
  return template.id;
}

function bespokeStatusTier(status: BespokeTemplateResponse['status']) {
  if (status === 'live') return 0;
  if (status === 'paused') return 1;
  return 2;
}

function countBespokeBulkTargets(
  templatesList: BespokeTemplateResponse[],
  direction: 'pause' | 'resume',
  pinsFilter: FormSchemaVersionPin[] | null
): number {
  let wants =
    direction === 'pause'
      ? templatesList.filter(t => t.status === 'live')
      : templatesList.filter(t => t.status === 'paused');
  if (pinsFilter != null) {
    wants = wants.filter(t =>
      pinsFilter.some(pin => bespokeTemplateMatchesPin(t, pin))
    );
  }
  return wants.length;
}

/** What the inline section editor is currently pointed at. */
interface EditorTarget {
  editingTemplate: BespokeTemplateResponse | null;
  draft: VibecoderDraft | null;
}

function blankEditorTargetFromDraft(draft: SectionEditorDraft): EditorTarget {
  return {
    editingTemplate: null,
    draft: {
      schemaVersionName: draft.schemaVersionName,
      schemaVersionValue: draft.schemaVersionValue,
      rogerDocFormType: draft.rogerDocFormType,
      sectionName: draft.sectionName,
      name: draft.name,
      description: draft.description,
      layoutJsonText: draft.layoutJsonText,
      layoutStatus: draft.status,
    },
  };
}

function editorTargetFromPersistedDraft(
  draft: SectionEditorDraft,
  templates: BespokeTemplateResponse[]
): EditorTarget {
  if (draft.editingTemplateId) {
    const template = templates.find(t => t.id === draft.editingTemplateId);
    if (template) {
      return { editingTemplate: template, draft: null };
    }
  }
  return blankEditorTargetFromDraft(draft);
}

const FormSchemas: React.FC = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
    }))
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const initialUrlStateRef = useRef({
    formType: searchParams.get(
      FORM_SCHEMA_QUERY_PARAMS.formType
    ) as RogerDocFormType | null,
    schemaName: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.schemaName),
    schemaVersion: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.schemaVersion),
    section: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.section),
    layoutTemplate: searchParams.get(FORM_SCHEMA_QUERY_PARAMS.layoutTemplate),
  });
  /** After first auto-select, only URL params matching the active form type apply. */
  const initialVersionUrlConsumedRef = useRef(false);
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const sectionGenerationStatusByKey = useStore(
    useShallow(state => state.sectionGenerationStatusByKey)
  );
  const startFormSchemaLayoutGeneration = useStore(
    state => state.startFormSchemaLayoutGeneration
  );

  // Per-section editor working drafts (survive section switches).
  const editorDraftByKey = useStore(
    useShallow(state => state.editorDraftByKey)
  );
  const hydrateSectionEditorDraft = useStore(
    state => state.hydrateSectionEditorDraft
  );
  const setSectionEditorDraft = useStore(state => state.setSectionEditorDraft);
  const setSectionEditorField = useStore(state => state.setSectionEditorField);
  const syncSectionEditorTemplateDetails = useStore(
    state => state.syncSectionEditorTemplateDetails
  );
  const markSectionEditorSaved = useStore(
    state => state.markSectionEditorSaved
  );

  const updateUrlSelection = useCallback(
    (next: {
      formType?: RogerDocFormType | null;
      schemaName?: string | null;
      schemaVersion?: string | null;
      section?: string | null;
      layoutTemplate?: string | null;
    }) => {
      setSearchParams(
        prev => {
          const params = new URLSearchParams(prev);
          const setOrDelete = (
            key: string,
            value: string | null | undefined
          ) => {
            if (value == null || value === '') params.delete(key);
            else params.set(key, value);
          };
          if ('formType' in next) {
            setOrDelete(FORM_SCHEMA_QUERY_PARAMS.formType, next.formType);
          }
          if ('schemaName' in next) {
            setOrDelete(FORM_SCHEMA_QUERY_PARAMS.schemaName, next.schemaName);
          }
          if ('schemaVersion' in next) {
            setOrDelete(
              FORM_SCHEMA_QUERY_PARAMS.schemaVersion,
              next.schemaVersion
            );
          }
          if ('section' in next) {
            setOrDelete(FORM_SCHEMA_QUERY_PARAMS.section, next.section);
          }
          if ('layoutTemplate' in next) {
            setOrDelete(
              FORM_SCHEMA_QUERY_PARAMS.layoutTemplate,
              next.layoutTemplate
            );
          }
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [formTypeCatalog, setFormTypeCatalog] = useState<
    RogerDocFormTypeCatalogEntry[]
  >([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  // -- state: schema versions browser --
  const [selectedRogerDocFormType, setSelectedRogerDocFormType] =
    useState<RogerDocFormType | null>(null);
  const [versions, setVersions] = useState<FormSchemaVersion[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);

  // -- state: form tree preview --
  const [selectedVersion, setSelectedVersion] =
    useState<FormSchemaVersion | null>(null);
  const [formTree, setFormTree] = useState<FormTree | null>(null);
  // Mirrors the FSV's `section_order` for the active form-tree preview, so
  // the studio sidebar/table use the same order + display labels the visit
  // UI does. `null` when the FSV doesn't ship one (older RogerDoc deploys).
  const [formTreeSectionOrder, setFormTreeSectionOrder] = useState<
    SectionOrderItem[] | null
  >(null);
  const [treeLoading, setTreeLoading] = useState(false);

  // -- state: bespoke templates --
  const [templates, setTemplates] = useState<BespokeTemplateResponse[]>([]);
  const [templatesReady, setTemplatesReady] = useState(false);
  // True when the last catalog fetch failed. The template list isn't
  // version-scoped, so schema-version switches no longer refetch it — but if
  // the initial load failed we still want the next version switch to retry
  // rather than leave the page permanently empty.
  const [templatesLoadFailed, setTemplatesLoadFailed] = useState(false);
  const [templateStatusBusyId, setTemplateStatusBusyId] = useState<
    string | null
  >(null);
  const [bulkBespokeDialog, setBulkBespokeDialog] = useState<{
    direction: 'pause' | 'resume';
    scopeSummary: string;
    pinsFilter: FormSchemaVersionPin[] | null;
    affectedCount: number;
  } | null>(null);
  const [bulkBespokeBusy, setBulkBespokeBusy] = useState(false);

  const selectedCatalogEntry = useMemo(
    () =>
      formTypeCatalog.find(r => r.form_type === selectedRogerDocFormType) ??
      null,
    [formTypeCatalog, selectedRogerDocFormType]
  );

  const canonicalVisitForApi: VisitTypeId | null =
    selectedCatalogEntry?.canonical_visit_type_id ?? null;

  const [editorTarget, setEditorTarget] = useState<EditorTarget | null>(null);
  const [versionsSection, setVersionsSection] = useState<string | null>(null);
  const [questionDialog, setQuestionDialog] = useState<{
    sectionName: string;
    questions: FormTreeQuestion[];
  } | null>(null);
  const [customGenerateDialog, setCustomGenerateDialog] = useState<{
    sectionName: string;
    displayName: string;
    questions: FormTreeQuestion[];
  } | null>(null);
  const [customGeneratePrompt, setCustomGeneratePrompt] = useState(
    DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT
  );
  const [selectedSectionName, setSelectedSectionName] = useState<string | null>(
    null
  );
  const [saving, setSaving] = useState(false);

  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [validationResultsOpen, setValidationResultsOpen] = useState(false);
  const [layoutJsonOpen, setLayoutJsonOpen] = useState(false);
  const [exportPrepLoading, setExportPrepLoading] = useState(false);
  const [exportLoadGeneration, setExportLoadGeneration] = useState(0);
  const [exportVersionsByFormType, setExportVersionsByFormType] = useState<
    Map<RogerDocFormType, FormSchemaVersion[]>
  >(() => new Map());
  const [exportCandidateRows, setExportCandidateRows] = useState<
    BespokeTemplateExportRow[]
  >([]);
  const [exportNestedGroups, setExportNestedGroups] = useState<
    ImportFormTypeGroup[]
  >([]);
  const [exportRowByKey, setExportRowByKey] = useState<
    Map<string, BespokeTemplateExportRow>
  >(() => new Map());
  const [exportSelectedLeafKeys, setExportSelectedLeafKeys] = useState<
    Set<string>
  >(() => new Set());
  const [exportInProgress, setExportInProgress] = useState(false);
  const exportSelectionInitGenRef = useRef(-1);

  const importFileRef = useRef<HTMLInputElement>(null);
  const importSelectionInitPickGenRef = useRef(-1);
  /** Last `importBundlePickGeneration` we've already warned about for unresolved legacy pins. */
  const importUnresolvedWarnedGenRef = useRef(-1);

  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importPrepLoading, setImportPrepLoading] = useState(false);
  const [importParseError, setImportParseError] = useState<string | null>(null);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importBundleSummary, setImportBundleSummary] = useState<{
    filename: string;
    formatVersion: number;
    exportScope?: string;
    exportedAt?: string;
    rowCount: number;
  } | null>(null);
  const [importSourceRows, setImportSourceRows] = useState<
    BespokeTemplateExportRow[]
  >([]);
  const [importVersionsByFormType, setImportVersionsByFormType] = useState<
    Map<RogerDocFormType, FormSchemaVersion[]>
  >(() => new Map());
  const [importNestedGroups, setImportNestedGroups] = useState<
    ImportFormTypeGroup[]
  >([]);
  const [importRowByKey, setImportRowByKey] = useState<
    Map<string, BespokeTemplateExportRow>
  >(() => new Map());
  const [importSelectedKeys, setImportSelectedKeys] = useState<Set<string>>(
    () => new Set()
  );
  /** Increments when a JSON bundle is successfully parsed — initial row selection uses this. */
  const [importBundlePickGeneration, setImportBundlePickGeneration] =
    useState(0);

  const [bulkMatchingCopyModalOpen, setBulkMatchingCopyModalOpen] =
    useState(false);

  useEffect(() => {
    if (!exportModalOpen || formTypeCatalog.length === 0) return;

    let cancelled = false;
    void (async () => {
      setExportPrepLoading(true);
      try {
        const m = new Map<RogerDocFormType, FormSchemaVersion[]>();
        await Promise.all(
          formTypeCatalog.map(async row => {
            const vers = await fetchRogerEmrFormSchemaVersions(
              row.canonical_visit_type_id
            );
            m.set(row.form_type as RogerDocFormType, vers);
          })
        );
        if (cancelled) return;

        const allTemplates = ((
          await listBespokeTemplatesBespokeTemplatesGet({
            throwOnError: true,
          })
        ).data ?? []) as BespokeTemplateResponse[];

        const allowed = collectFormSchemaVersionPinsForFormTypes({
          formTypes: formTypeCatalog.map(
            row => row.form_type as RogerDocFormType
          ),
          versionsByFormType: m,
        });

        const poolRows = filterBespokeTemplatesForExport({
          allTemplates,
          allowedFormSchemaVersionPinKeys: allowed,
        });

        setExportVersionsByFormType(m);
        setExportCandidateRows(poolRows);
        setExportLoadGeneration(g => g + 1);
      } catch (err) {
        console.error('Export modal: failed to load preview data:', err);
        if (!cancelled) {
          toastRef.current.error(
            'Could not prepare export',
            'Close and reopen Export, or check your connection.'
          );
          setExportVersionsByFormType(new Map());
          setExportCandidateRows([]);
        }
      } finally {
        if (!cancelled) {
          setExportPrepLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exportModalOpen, formTypeCatalog]);

  useEffect(() => {
    if (!importModalOpen || formTypeCatalog.length === 0) return;
    let cancelled = false;
    void (async () => {
      setImportPrepLoading(true);
      /** Avoid grouping against a stale slice from a previous dialog session. */
      setImportVersionsByFormType(new Map());
      try {
        const m = new Map<RogerDocFormType, FormSchemaVersion[]>();
        await Promise.all(
          formTypeCatalog.map(async row => {
            const vers = await fetchRogerEmrFormSchemaVersions(
              row.canonical_visit_type_id
            );
            m.set(row.form_type as RogerDocFormType, vers);
          })
        );
        if (!cancelled) {
          setImportVersionsByFormType(m);
        }
      } catch (err) {
        console.error('Import modal: failed to load schema versions:', err);
        if (!cancelled) {
          toastRef.current.error(
            'Could not load schema versions',
            'Grouping may be incomplete until you reopen the import dialog.'
          );
          setImportVersionsByFormType(new Map());
        }
      } finally {
        if (!cancelled) {
          setImportPrepLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [importModalOpen, formTypeCatalog]);

  /** When the import dialog opens or closes, reset its client state (avoids stale filename / trees). */
  useEffect(() => {
    if (!importModalOpen) {
      setImportPrepLoading(false);
      setImportVersionsByFormType(new Map());
    }
    setImportParseError(null);
    setImportBundleSummary(null);
    setImportSourceRows([]);
    setImportNestedGroups([]);
    setImportRowByKey(new Map());
    setImportSelectedKeys(new Set());
    setImportSubmitting(false);
    setImportBundlePickGeneration(0);
    importSelectionInitPickGenRef.current = -1;
    importUnresolvedWarnedGenRef.current = -1;
    if (importFileRef.current) {
      importFileRef.current.value = '';
    }
  }, [importModalOpen]);

  useEffect(() => {
    if (!importModalOpen) return;
    if (importSourceRows.length === 0) {
      importSelectionInitPickGenRef.current = -1;
      setImportNestedGroups([]);
      setImportRowByKey(new Map());
      setImportSelectedKeys(new Set());
      return;
    }
    if (importPrepLoading || formTypeCatalog.length === 0) {
      setImportNestedGroups([]);
      setImportRowByKey(new Map());
      setImportSelectedKeys(new Set());
      return;
    }

    // Legacy bundles (format_version <= 4) don't carry form_type/form_schema_version
    // per row — infer them from the catalog. Ambiguous/unknown names are dropped and
    // reported so the user can fix the bundle instead of importing a guessed pin.
    const { rows: resolvedRows, unresolved } = resolveLegacyImportRowPins({
      rows: importSourceRows,
      catalog: formTypeCatalog,
      versionsByFormType: importVersionsByFormType,
    });
    if (
      unresolved.length > 0 &&
      importUnresolvedWarnedGenRef.current !== importBundlePickGeneration
    ) {
      importUnresolvedWarnedGenRef.current = importBundlePickGeneration;
      const names = [...new Set(unresolved.map(r => r.schema_version_name))];
      toastRef.current.error(
        'Some rows could not be matched to a form type',
        `${unresolved.length} row(s) skipped (schema version name unknown or shared by multiple form types): ${names.slice(0, 3).join(', ')}${names.length > 3 ? ` (+${names.length - 3} more)` : ''}. Re-export from a current environment to include a form_type on each row.`
      );
    }

    const groups = buildImportNestedGroups({
      templates: resolvedRows,
      catalog: formTypeCatalog,
    });
    setImportNestedGroups(groups);
    const allKeys = collectLeafKeysFromGroups(groups);
    setImportRowByKey(bespokeRowsByLeafKey(resolvedRows));

    const isNewParsedBundle =
      importSelectionInitPickGenRef.current !== importBundlePickGeneration;
    if (isNewParsedBundle) {
      importSelectionInitPickGenRef.current = importBundlePickGeneration;
    }

    setImportSelectedKeys(prev => {
      if (isNewParsedBundle) {
        return new Set(allKeys);
      }
      const next = new Set<string>();
      for (const k of allKeys) {
        if (prev.has(k)) next.add(k);
      }
      return next;
    });
  }, [
    formTypeCatalog,
    importBundlePickGeneration,
    importModalOpen,
    importPrepLoading,
    importSourceRows,
    importVersionsByFormType,
  ]);

  useEffect(() => {
    if (!exportModalOpen) return;
    if (exportCandidateRows.length === 0) {
      exportSelectionInitGenRef.current = -1;
      setExportNestedGroups([]);
      setExportRowByKey(new Map());
      setExportSelectedLeafKeys(new Set());
      return;
    }
    const groups = buildImportNestedGroups({
      templates: exportCandidateRows,
      catalog: formTypeCatalog,
    });
    setExportNestedGroups(groups);
    const allKeys = collectLeafKeysFromGroups(groups);
    setExportRowByKey(bespokeRowsByLeafKey(exportCandidateRows));

    const isNewPool =
      exportSelectionInitGenRef.current !== exportLoadGeneration;
    if (isNewPool) {
      exportSelectionInitGenRef.current = exportLoadGeneration;
    }

    setExportSelectedLeafKeys(prev => {
      if (isNewPool) {
        return new Set(allKeys);
      }
      const next = new Set<string>();
      for (const k of allKeys) {
        if (prev.has(k)) next.add(k);
      }
      return next;
    });
  }, [
    exportCandidateRows,
    exportLoadGeneration,
    exportModalOpen,
    exportVersionsByFormType,
    formTypeCatalog,
  ]);

  const exportSelectableUiRows = useMemo(
    () =>
      importNestedGroupsToSelectableNestedRows(
        exportNestedGroups,
        formatSectionDropdownLabel
      ),
    [exportNestedGroups]
  );

  const exportAllLeafKeys = useMemo(
    () => collectLeafKeysFromGroups(exportNestedGroups),
    [exportNestedGroups]
  );

  const handleRunBespokeExport = useCallback(async () => {
    if (exportSelectedLeafKeys.size === 0) {
      toast.error('Nothing to export', 'Select at least one template row.');
      return;
    }

    const chosen: BespokeTemplateExportRow[] = [];
    for (const k of exportSelectedLeafKeys) {
      const row = exportRowByKey.get(k);
      if (row) chosen.push(row);
    }
    if (chosen.length === 0) {
      toast.error(
        'Nothing to export',
        'Selection did not resolve to any rows — reopen the dialog.'
      );
      return;
    }

    setExportInProgress(true);
    try {
      const bundle = buildBespokeTemplatesExportBundleFromChosenTemplates({
        templates: chosen,
      });

      downloadJsonFile(buildBespokeExportFilename({}), bundle);

      toast.success(
        'Export ready',
        bundle.templates.length === 0
          ? 'JSON downloaded (no templates in selection).'
          : `Downloaded ${bundle.templates.length} bespoke template row(s).`
      );
      setExportModalOpen(false);
    } catch (err) {
      console.error('Bespoke template export failed:', err);
      toast.error('Export failed', bespokeApiErrorMessage(err));
    } finally {
      setExportInProgress(false);
    }
  }, [
    exportRowByKey,
    exportSelectedLeafKeys,
    exportVersionsByFormType,
    formTypeCatalog,
    toast,
  ]);

  useEffect(() => {
    if (exportModalOpen) return;
    setExportPrepLoading(false);
    setExportLoadGeneration(0);
    exportSelectionInitGenRef.current = -1;
    setExportVersionsByFormType(new Map());
    setExportCandidateRows([]);
    setExportNestedGroups([]);
    setExportRowByKey(new Map());
    setExportSelectedLeafKeys(new Set());
    setExportInProgress(false);
  }, [exportModalOpen]);

  const handleImportBundleFileChosen = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const file = input.files?.[0];
      if (!file) return;
      setImportParseError(null);

      try {
        const text = await file.text();
        const json: unknown = JSON.parse(text);
        const parsed = parseBespokeImportBundleJson(json);

        const filtered = parsed.templates;

        if (filtered.length === 0) {
          setImportParseError('The bundle contains no templates.');
          setImportBundleSummary(null);
          setImportSourceRows([]);
          return;
        }

        setImportBundleSummary({
          filename: file.name,
          formatVersion: parsed.formatVersion,
          exportScope: parsed.exportScope,
          exportedAt: parsed.exportedAt,
          rowCount: filtered.length,
        });
        setImportSourceRows(filtered);
        setImportBundlePickGeneration(g => g + 1);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Could not read import file.';
        setImportParseError(msg);
        setImportBundleSummary(null);
        setImportSourceRows([]);
      } finally {
        input.value = '';
      }
    },
    []
  );

  const getCanonicalVisitForFormType = useCallback(
    (formType: RogerDocFormType | null | undefined): VisitTypeId | null =>
      formTypeCatalog.find(row => row.form_type === formType)
        ?.canonical_visit_type_id ?? null,
    [formTypeCatalog]
  );

  // -- fetch schema versions when visit type changes --
  const fetchVersions = useCallback(async (visitTypeApi: VisitTypeId) => {
    setVersionsLoading(true);
    setVersionsError(null);
    setSelectedVersion(null);
    setFormTree(null);
    setFormTreeSectionOrder(null);
    try {
      setVersions(await fetchRogerEmrFormSchemaVersions(visitTypeApi));
    } catch (err) {
      console.error('Failed to fetch schema versions:', err);
      setVersions([]);
      setVersionsError(
        bespokeApiErrorMessage(err) ||
          'Could not load schema versions for this form type.'
      );
    } finally {
      setVersionsLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setCatalogLoading(true);
      try {
        const res = await getRogerDocFormTypeCatalogRogerdocFormTypeCatalogGet(
          {}
        );
        const rows =
          (res?.data as RogerDocFormTypeCatalogEntry[] | undefined) ?? [];
        if (cancelled) return;
        setFormTypeCatalog(rows);
        setSelectedRogerDocFormType(prev => {
          if (prev != null && rows.some(r => r.form_type === prev)) {
            return prev;
          }
          const urlFormType = initialUrlStateRef.current.formType;
          if (
            urlFormType != null &&
            rows.some(r => r.form_type === urlFormType)
          ) {
            return urlFormType;
          }
          return rows[0]?.form_type ?? null;
        });
      } catch (err) {
        console.error('Failed to load Roger Doc form type catalog:', err);
        if (!cancelled) {
          setFormTypeCatalog([]);
          setSelectedRogerDocFormType(null);
          toastRef.current.error(
            'Catalog unavailable',
            'Could not load Roger Doc form types. Refresh to retry.'
          );
        }
      } finally {
        if (!cancelled) {
          setCatalogLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (canonicalVisitForApi == null) return;
    void fetchVersions(canonicalVisitForApi);
  }, [canonicalVisitForApi, fetchVersions]);

  /**
   * `section_id → display_name` for the active form tree (ROG-2295). Empty
   * when the FSV doesn't ship `section_order`; `formatSectionDropdownLabel`
   * falls back to the legacy underscore-strip transform in that case.
   */
  const formTreeLabelByCategory = useMemo(() => {
    const lookup: Record<string, string> = {};
    for (const item of formTreeSectionOrder ?? []) {
      lookup[item.section_id] = item.display_name;
    }
    return lookup;
  }, [formTreeSectionOrder]);

  /**
   * Section keys for the active form tree in the FSV's preferred order.
   * Falls back to `Object.keys(formTree)` when no section_order is supplied
   * so studio behavior is unchanged for legacy FSVs.
   */
  const orderedFormTreeSections = useMemo<string[]>(() => {
    if (!formTree) return [];
    return orderedFormTreeSectionIds(formTree, formTreeSectionOrder);
  }, [formTree, formTreeSectionOrder]);

  useEffect(() => {
    if (!formTree) {
      setSelectedSectionName(null);
      return;
    }

    const sections = orderedFormTreeSections;
    setSelectedSectionName(prev =>
      initialUrlStateRef.current.section &&
      sections.includes(initialUrlStateRef.current.section)
        ? initialUrlStateRef.current.section
        : prev && sections.includes(prev)
          ? prev
          : (sections[0] ?? null)
    );
  }, [formTree, orderedFormTreeSections]);

  // -- fetch the form tree for a selected version --
  // The build endpoint returns {version, schema_data} for a specific
  // (name, version) selector; we use schema_data as the form tree, plus
  // version.section_order to drive section order + display labels.
  const fetchFormTreeData = useCallback(
    async (
      version: FormSchemaVersion
    ): Promise<{
      tree: FormTree | null;
      sectionOrder: SectionOrderItem[] | null;
    }> => {
      const visitTypeApi =
        getCanonicalVisitForFormType(version.form_type as RogerDocFormType) ??
        canonicalVisitForApi;
      if (visitTypeApi == null) return { tree: null, sectionOrder: null };
      const res = await getVersionedQuestionSchemaRogerdocV2QuestionSchemaPost({
        query: { visit_type: visitTypeApi },
        body: {
          schema_version: {
            name: version.name,
            version: version.version ?? null,
          },
        },
      });
      const payload = res?.data as VersionedQuestionSchema | undefined;
      if (!payload) return { tree: null, sectionOrder: null };
      return {
        tree: payload.schema_data as FormTree,
        sectionOrder: payload.version.section_order ?? null,
      };
    },
    [canonicalVisitForApi, getCanonicalVisitForFormType]
  );

  const fetchFormTree = useCallback(
    async (version: FormSchemaVersion) => {
      setTreeLoading(true);
      try {
        const { tree, sectionOrder } = await fetchFormTreeData(version);
        setFormTree(tree);
        setFormTreeSectionOrder(sectionOrder);
      } catch (err) {
        console.error('Failed to fetch form tree:', err);
        setFormTree(null);
        setFormTreeSectionOrder(null);
      } finally {
        setTreeLoading(false);
      }
    },
    [fetchFormTreeData]
  );

  // -- fetch bespoke templates --
  // Returns the fetched list (in addition to setting state) so callers that
  // need the fresh rows synchronously — e.g. the live-refresh poll below —
  // don't have to wait a render cycle for `templates` to catch up.
  const fetchTemplates = useCallback(async (): Promise<
    BespokeTemplateResponse[]
  > => {
    try {
      const res = await listBespokeTemplatesBespokeTemplatesGet({
        throwOnError: true,
      });
      const list = (res?.data as BespokeTemplateResponse[] | undefined) ?? [];
      setTemplates(list);
      setTemplatesLoadFailed(false);
      return list;
    } catch (err) {
      console.error('Failed to fetch bespoke templates:', err);
      setTemplates([]);
      setTemplatesLoadFailed(true);
      return [];
    } finally {
      setTemplatesReady(true);
    }
  }, []);

  /**
   * Retry the catalog load only when the previous fetch actually failed. Keeps
   * healthy schema-version switches refetch-free (the list isn't version-scoped)
   * while giving a page that failed its first load a second chance instead of
   * staying empty forever.
   */
  const retryTemplatesIfLoadFailed = useCallback(() => {
    if (templatesLoadFailed) void fetchTemplates();
  }, [fetchTemplates, templatesLoadFailed]);

  /**
   * Apply the response of a single-row create/update directly to local
   * state instead of refetching the entire (unscoped) template list.
   *
   * The list from `fetchTemplates()` isn't version/pin-scoped — it's every
   * bespoke template everywhere — so refetching it after a one-row mutation
   * replaces every *other* row with a brand-new object too. That churns
   * memoized derivations (`enabledTemplateBySection`, `editorTarget`, ...)
   * and re-triggers effects downstream that key off object identity rather
   * than content (e.g. the open section editor's RogerDoc schema fetch),
   * causing an avoidable RogerDoc round-trip after every single-section
   * copy or edit.
   *
   * Exception: when the prior catalog load failed, local state is empty and
   * untrusted — patching in just the saved row would make the page look like
   * a one-template catalog. Heal with a full `fetchTemplates()` instead.
   *
   * `upsertBespokeTemplatesLocal` also mirrors the backend's single-primary
   * rule so promoting/creating a live row demotes the section's stale active
   * row locally (see the helper for details).
   */
  const upsertTemplatesLocal = useCallback(
    (saved: readonly BespokeTemplateResponse[]) => {
      if (saved.length === 0) return;
      if (templatesLoadFailed) {
        void fetchTemplates();
        return;
      }
      setTemplates(prev => upsertBespokeTemplatesLocal(prev, saved));
    },
    [fetchTemplates, templatesLoadFailed]
  );

  const upsertTemplateLocal = useCallback(
    (saved: BespokeTemplateResponse) => upsertTemplatesLocal([saved]),
    [upsertTemplatesLocal]
  );

  const removeTemplateLocal = useCallback((id: string) => {
    setTemplates(prev => removeBespokeTemplateLocal(prev, id));
  }, []);

  const handleRunBespokeImport = useCallback(async () => {
    const rows = [...importSelectedKeys]
      .map(k => importRowByKey.get(k))
      .filter((r): r is BespokeTemplateExportRow => r != null);

    if (rows.length === 0) {
      toast.error(
        'Nothing to import',
        'Select at least one template checkbox.'
      );
      return;
    }

    setImportSubmitting(true);
    try {
      const res =
        await importBespokeTemplatesBundleBespokeTemplatesImportBundlePost({
          throwOnError: true,
          body: {
            templates: exportRowsToImportUpserts(rows),
          },
        });
      const created = res.data?.created_count ?? 0;
      const updated = res.data?.updated_count ?? 0;

      toast.success(
        'Import complete',
        `${created} created, ${updated} updated (always upserts).`
      );
      fetchTemplates().catch(err =>
        console.error('Failed to refresh templates after import:', err)
      );
      setImportModalOpen(false);
    } catch (err) {
      console.error('Bespoke template import failed:', err);
      toast.error('Import failed', bespokeApiErrorMessage(err));
    } finally {
      setImportSubmitting(false);
    }
  }, [fetchTemplates, importRowByKey, importSelectedKeys, toast]);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (selectedVersion || versionsLoading || versions.length === 0) return;
    if (
      selectedRogerDocFormType != null &&
      versions.some(v => v.form_type !== selectedRogerDocFormType)
    ) {
      return;
    }

    const urlMatchesFormType =
      !initialVersionUrlConsumedRef.current &&
      initialUrlStateRef.current.formType === selectedRogerDocFormType;
    const { schemaName, schemaVersion } = urlMatchesFormType
      ? initialUrlStateRef.current
      : { schemaName: null, schemaVersion: null };
    const match = pickInitialVersion(versions, { schemaName, schemaVersion });
    if (!match) return;
    initialVersionUrlConsumedRef.current = true;
    setSelectedVersion(match);
    void fetchFormTree(match);
    // NOTE: no fetchTemplates() here — the bespoke template list isn't
    // version/pin-scoped (it's every template everywhere), so switching the
    // selected schema version never changes what that list should contain.
  }, [
    fetchFormTree,
    selectedRogerDocFormType,
    selectedVersion,
    versions,
    versionsLoading,
  ]);

  const handleVersionClick = (v: FormSchemaVersion) => {
    setSelectedVersion(v);
    setSelectedSectionName(null);
    updateUrlSelection({
      formType: v.form_type ? (v.form_type as RogerDocFormType) : null,
      schemaName: v.name,
      schemaVersion: `${v.version ?? ''}`,
      section: null,
      layoutTemplate: null,
    });
    void fetchFormTree(v);
    retryTemplatesIfLoadFailed();
  };

  const openBulkMatchingCopyForSchemaVersion = useCallback(
    (v: FormSchemaVersion) => {
      void (async () => {
        const sameSlot =
          selectedVersion?.name === v.name &&
          `${selectedVersion?.version ?? ''}` === `${v.version ?? ''}`;
        if (!sameSlot) {
          setSelectedVersion(v);
          setSelectedSectionName(null);
          updateUrlSelection({
            formType: v.form_type ? (v.form_type as RogerDocFormType) : null,
            schemaName: v.name,
            schemaVersion: `${v.version ?? ''}`,
            section: null,
            layoutTemplate: null,
          });
        }
        const hasTree =
          sameSlot && formTree != null && Object.keys(formTree).length > 0;
        if (!hasTree) {
          await fetchFormTree(v);
        }
        retryTemplatesIfLoadFailed();
        setBulkMatchingCopyModalOpen(true);
      })();
    },
    [
      fetchFormTree,
      formTree,
      retryTemplatesIfLoadFailed,
      selectedVersion,
      updateUrlSelection,
    ]
  );

  const handleSectionClick = (sectionName: string) => {
    setLayoutJsonOpen(false);
    setSelectedSectionName(sectionName);
    updateUrlSelection({ section: sectionName, layoutTemplate: null });
  };

  // -- bespoke CRUD handlers --
  const handlePersistVibecoderJson = async (
    payload: BespokeTemplateCreate | BespokeTemplateUpdate
  ) => {
    if (!selectedVersion || !selectedSectionName || !selectedRogerDocFormType)
      return;
    const key = sectionEditorDraftKey(
      selectedRogerDocFormType,
      selectedVersion.name,
      selectedSectionName
    );
    const draft = editorDraftByKey[key];
    const templateId = draft?.editingTemplateId ?? null;
    const submittedSnapshot = draft
      ? {
          layoutJsonText: draft.layoutJsonText,
          name: draft.name,
          description: draft.description,
          status: draft.status,
        }
      : undefined;
    setSaving(true);
    try {
      let saved: BespokeTemplateResponse;
      if (templateId) {
        const res = await updateBespokeTemplateBespokeTemplatesTemplateIdPatch({
          path: { template_id: templateId },
          body: payload as BespokeTemplateUpdate,
          throwOnError: true,
        });
        // Fail closed before celebrating success: without the saved row we
        // can't patch local state and the list would keep the stale active row.
        saved = requireSavedBespokeTemplate(res?.data);
        toast.success('Template Updated', 'Roger Layout Schema saved.');
      } else {
        const res = await createBespokeTemplateBespokeTemplatesPost({
          body: payload as BespokeTemplateCreate,
          throwOnError: true,
        });
        saved = requireSavedBespokeTemplate(res?.data);
        toast.success('Template Created', 'JSON bespoke template registered.');
      }
      // A local save's own `updated_at` bump is not a remote push: suppress it
      // for the next poll cycle and drop any stale push entry immediately so
      // the dot/pill don't show against content we just saved ourselves.
      ownSaveTemplateIdsRef.current.add(saved.id);
      setRemotePushByTemplateId(prev => clearRemotePush(prev, saved.id));
      setEditorTarget({ editingTemplate: saved, draft: null });
      if (selectedVersion) {
        markSectionEditorSaved(
          sectionEditorDraftKey(
            selectedRogerDocFormType,
            selectedVersion.name,
            saved.section_name
          ),
          saved,
          submittedSnapshot
        );
      }
      const active = enabledTemplateBySection.get(saved.section_name) ?? null;
      const nextActive = saved.status === 'live' ? saved : active;
      updateUrlSelection({
        layoutTemplate: layoutTemplateQueryValue(saved, nextActive),
      });
      upsertTemplateLocal(saved);
    } catch (err) {
      console.error('Failed to persist JSON bespoke template:', err);
      toast.error('Save Failed', bespokeApiErrorMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteBespokeTemplateBespokeTemplatesTemplateIdDelete({
        path: { template_id: id },
        throwOnError: true,
      });
      toast.success('Template Deleted', 'Bespoke template removed.');
      removeTemplateLocal(id);
    } catch (err) {
      console.error('Failed to delete bespoke template:', err);
      toast.error('Delete Failed', bespokeApiErrorMessage(err));
    }
  };

  const handleSetBespokeTemplateStatus = async (
    t: BespokeTemplateResponse,
    nextStatus: BespokeTemplateResponse['status']
  ) => {
    if (t.status === nextStatus) return;
    const demotedSiblingIds =
      nextStatus === 'live' || nextStatus === 'paused'
        ? demotedPrimarySiblingIdsForPromotion(templates, t)
        : [];
    setTemplateStatusBusyId(t.id);
    try {
      const res = await updateBespokeTemplateBespokeTemplatesTemplateIdPatch({
        path: { template_id: t.id },
        body: { status: nextStatus },
        throwOnError: true,
      });
      // Fail closed: the server may have promoted this row and demoted a
      // sibling, so a missing body must surface as a failure rather than leave
      // the list showing the old active layout.
      const saved = requireSavedBespokeTemplate(res?.data);
      ownSaveTemplateIdsRef.current.add(saved.id);
      for (const id of demotedSiblingIds) {
        ownSaveTemplateIdsRef.current.add(id);
      }
      setRemotePushByTemplateId(prev => {
        let next = clearRemotePush(prev, saved.id);
        for (const id of demotedSiblingIds) {
          next = clearRemotePush(next, id);
        }
        return next;
      });
      upsertTemplateLocal(saved);
    } catch (err) {
      console.error('Failed to update bespoke template status:', err);
      toast.error('Update Failed', bespokeApiErrorMessage(err));
    } finally {
      setTemplateStatusBusyId(null);
    }
  };

  const handleSaveTemplateDetails = async (
    template: BespokeTemplateResponse,
    details: { name: string; description: string }
  ) => {
    setTemplateStatusBusyId(template.id);
    try {
      const res = await updateBespokeTemplateBespokeTemplatesTemplateIdPatch({
        path: { template_id: template.id },
        body: {
          name: details.name,
          description: details.description,
        },
        throwOnError: true,
      });
      // Fail closed before toasting success so the list can't silently drift.
      const saved = requireSavedBespokeTemplate(res?.data);
      ownSaveTemplateIdsRef.current.add(saved.id);
      setRemotePushByTemplateId(prev => clearRemotePush(prev, saved.id));
      const sectionName = template.section_name?.trim();
      if (sectionName && selectedRogerDocFormType) {
        const key = sectionEditorDraftKey(
          selectedRogerDocFormType,
          template.schema_version_name,
          sectionName
        );
        const draft = useStore.getState().editorDraftByKey[key];
        if (draft?.editingTemplateId === template.id) {
          syncSectionEditorTemplateDetails(key, details);
        }
      }
      toast.success('Template updated', 'Name and description saved.');
      upsertTemplateLocal(saved);
    } catch (err) {
      console.error('Failed to update template details:', err);
      toast.error('Update failed', bespokeApiErrorMessage(err));
      throw err;
    } finally {
      setTemplateStatusBusyId(null);
    }
  };

  const pinsForCurrentFormType = useMemo(() => {
    if (!selectedRogerDocFormType) return [];
    return pinsFromFormSchemaVersions(selectedRogerDocFormType, versions);
  }, [selectedRogerDocFormType, versions]);

  const openBulkBespokeDialog = useCallback(
    (
      direction: 'pause' | 'resume',
      scopeSummary: string,
      pinsFilter: FormSchemaVersionPin[] | null
    ) => {
      const affectedCount = countBespokeBulkTargets(
        templates,
        direction,
        pinsFilter
      );
      setBulkBespokeDialog({
        direction,
        scopeSummary,
        pinsFilter,
        affectedCount,
      });
    },
    [templates]
  );

  const runBulkBespokeConfirmed = useCallback(async () => {
    const d = bulkBespokeDialog;
    if (!d || d.affectedCount === 0) return;
    setBulkBespokeBusy(true);
    try {
      const res =
        await bespokeTemplatesBulkRenderStatusBespokeTemplatesBulkRenderStatusPost(
          {
            throwOnError: true,
            body: {
              direction: d.direction,
              ...(d.pinsFilter == null ? {} : { pins: d.pinsFilter }),
            },
          }
        );
      const n = res.data?.updated_count ?? 0;
      toast.success(
        'Bulk update complete',
        `${n} template${n === 1 ? '' : 's'} updated (Standby rows unchanged).`
      );
      setBulkBespokeDialog(null);
      await fetchTemplates();
    } catch (err) {
      console.error(err);
      toast.error('Bulk update failed', bespokeApiErrorMessage(err));
    } finally {
      setBulkBespokeBusy(false);
    }
  }, [bulkBespokeDialog, fetchTemplates, toast]);

  const importAllLeafKeys = useMemo(
    () => collectLeafKeysFromGroups(importNestedGroups),
    [importNestedGroups]
  );

  const importSelectableRows = useMemo(
    () =>
      importNestedGroupsToSelectableNestedRows(
        importNestedGroups,
        formatSectionDropdownLabel
      ),
    [importNestedGroups]
  );

  /** Full RogerDoc pin for the selected form type + schema version. */
  const selectedPin = useMemo(() => {
    if (!selectedVersion || !selectedRogerDocFormType) return null;
    return pinFromFormSchemaVersion(
      selectedRogerDocFormType as RogerDocFormType,
      selectedVersion
    );
  }, [selectedRogerDocFormType, selectedVersion]);

  /** Rows for the selected pin (form_type disambiguates shared schema names). */
  const visitTypeTemplates = useMemo(() => {
    if (!selectedPin) return [];
    return filterBespokeTemplatesForPin(templates, selectedPin);
  }, [selectedPin, templates]);

  /** Primary Live or Paused template per section (Live wins) for layout-tree status. */
  const enabledTemplateBySection = useMemo(() => {
    if (!selectedPin) return new Map<string, BespokeTemplateResponse>();
    return enabledTemplatesBySectionForPin(visitTypeTemplates, selectedPin);
  }, [selectedPin, visitTypeTemplates]);

  const existingTemplateBySection = useMemo(() => {
    const map = new Map<string, BespokeTemplateResponse>();
    for (const template of visitTypeTemplates) {
      if (template.section_name && !map.has(template.section_name)) {
        map.set(template.section_name, template);
      }
    }
    return map;
  }, [visitTypeTemplates]);

  /** Active template id for the selected section (null = none / blank default). */
  const selectedSectionDefaultTemplateId = selectedSectionName
    ? (enabledTemplateBySection.get(selectedSectionName)?.id ?? null)
    : null;

  /** Build the store-draft seed for a section from a template (or blank). */
  const buildSectionEditorSeed = useCallback(
    (
      sectionName: string,
      template: BespokeTemplateResponse | null
    ): SectionEditorDraftSeed | null => {
      if (!selectedVersion || !selectedRogerDocFormType) return null;
      const identity = {
        schemaVersionName: selectedVersion.name,
        schemaVersionValue: selectedVersion.version ?? null,
        rogerDocFormType: selectedRogerDocFormType,
        sectionName,
      };
      if (template) {
        return {
          ...identity,
          editingTemplateId: template.id,
          layoutJsonText: template.layout_json
            ? JSON.stringify(template.layout_json, null, 2)
            : '[]',
          name: template.name ?? '',
          description: template.description ?? '',
          status: template.status,
        };
      }
      return {
        ...identity,
        editingTemplateId: null,
        layoutJsonText: '[]',
        name: defaultBlankSectionLayoutName(
          formatSectionDropdownLabel(sectionName, formTreeLabelByCategory)
        ),
        description: '',
        status: 'live',
      };
    },
    [selectedVersion, selectedRogerDocFormType, formTreeLabelByCategory]
  );

  // Open the selected section's editor. Per-section drafts survive section
  // switches for the active template; returning to a section without
  // `layoutTemplate` in the URL defaults to active (not a standby/inactive row).
  // Waits for the bespoke catalog fetch so we do not seed a blank draft while
  // templates are still loading.
  useEffect(() => {
    if (!selectedVersion || !selectedRogerDocFormType || !selectedSectionName) {
      setEditorTarget(null);
      return;
    }
    if (!templatesReady) {
      return;
    }

    const key = sectionEditorDraftKey(
      selectedRogerDocFormType,
      selectedVersion.name,
      selectedSectionName
    );
    const active = enabledTemplateBySection.get(selectedSectionName) ?? null;
    const existingDraft = useStore.getState().editorDraftByKey[key];

    if (
      existingDraft &&
      shouldAdoptActiveTemplateDraft(existingDraft, active)
    ) {
      const seed = buildSectionEditorSeed(selectedSectionName, active);
      if (seed) {
        setSectionEditorDraft(key, seed);
        setEditorTarget({ editingTemplate: active, draft: null });
      }
      updateUrlSelection({ layoutTemplate: null });
      return;
    }

    const layoutTemplateId = searchParams.get(
      FORM_SCHEMA_QUERY_PARAMS.layoutTemplate
    );
    if (layoutTemplateId) {
      const urlTemplate =
        visitTypeTemplates.find(
          t =>
            t.id === layoutTemplateId && t.section_name === selectedSectionName
        ) ?? null;
      if (urlTemplate) {
        if (active?.id === urlTemplate.id) {
          updateUrlSelection({ layoutTemplate: null });
        } else {
          setEditorTarget({ editingTemplate: urlTemplate, draft: null });
          const seed = buildSectionEditorSeed(selectedSectionName, urlTemplate);
          if (seed) {
            if (
              !existingDraft ||
              existingDraft.editingTemplateId !== urlTemplate.id
            ) {
              if (existingDraft) {
                setSectionEditorDraft(key, seed);
              } else {
                hydrateSectionEditorDraft({ key, seed });
              }
            }
          }
          return;
        }
      } else {
        updateUrlSelection({ layoutTemplate: null });
      }
    }

    if (
      !layoutTemplateId &&
      existingDraft &&
      isPersistedInactiveLayoutDraft(existingDraft, active)
    ) {
      const seed = buildSectionEditorSeed(selectedSectionName, active);
      if (seed) {
        setSectionEditorDraft(key, seed);
        if (active) {
          setEditorTarget({ editingTemplate: active, draft: null });
        } else {
          setEditorTarget({
            editingTemplate: null,
            draft: {
              schemaVersionName: selectedVersion.name,
              schemaVersionValue: selectedVersion.version,
              rogerDocFormType: selectedRogerDocFormType,
              sectionName: selectedSectionName,
              name: seed.name,
              description: seed.description,
              layoutJsonText: seed.layoutJsonText,
              layoutStatus: seed.status,
            },
          });
        }
      }
      return;
    }

    if (existingDraft) {
      setEditorTarget(editorTargetFromPersistedDraft(existingDraft, templates));
      return;
    }

    if (active) {
      setEditorTarget({ editingTemplate: active, draft: null });
    } else {
      setEditorTarget({
        editingTemplate: null,
        draft: {
          schemaVersionName: selectedVersion.name,
          schemaVersionValue: selectedVersion.version,
          rogerDocFormType: selectedRogerDocFormType,
          sectionName: selectedSectionName,
          name: defaultBlankSectionLayoutName(
            formatSectionDropdownLabel(
              selectedSectionName,
              formTreeLabelByCategory
            )
          ),
          description: '',
          layoutJsonText: '[]',
          layoutStatus: 'live',
        },
      });
    }
    const seed = buildSectionEditorSeed(selectedSectionName, active);
    if (seed) hydrateSectionEditorDraft({ key, seed });
  }, [
    selectedSectionName,
    selectedVersion?.name,
    selectedVersion?.version,
    selectedRogerDocFormType,
    selectedSectionDefaultTemplateId,
    templatesReady,
    enabledTemplateBySection,
    visitTypeTemplates,
    templates,
    searchParams,
    buildSectionEditorSeed,
    hydrateSectionEditorDraft,
    setSectionEditorDraft,
    updateUrlSelection,
    formTreeLabelByCategory,
  ]);

  // -- live-refresh polling (EMR-277) --
  // Forms Manager MCP can push a standby layout draft from outside this tab.
  // Poll the lightweight revisions endpoint every few seconds and, on a real
  // change, refresh the catalog and (if the open editor isn't mid-edit) the
  // currently-open draft — so a pushed change shows up without a manual
  // reload. `null` means "no poll response seen yet"; distinct from any real
  // fingerprint (including the empty-list fingerprint `''`).
  const revisionsFingerprintRef = useRef<string | null>(null);

  // Per-template MCP-push tracking for the section-tab dot / versions-modal
  // dot / editor-header pill (see the UI-indicator helpers in
  // formSchemaUtils.ts). `remotePushSeenRevisionRef` is the diff baseline —
  // each row's content signature (and `updated_at` for push timestamps) the
  // last time we saw it — and `ownSaveTemplateIdsRef` is the one-poll-cycle
  // suppression window for ids this tab just saved itself, so our own save
  // isn't misread as a push.
  const [remotePushByTemplateId, setRemotePushByTemplateId] =
    useState<RemotePushByTemplateId>({});
  const remotePushSeenRevisionRef = useRef<
    Record<string, RemotePushRevisionSnapshot>
  >({});
  const remotePushHasBaselineRef = useRef(false);
  const ownSaveTemplateIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Reset so a form-type/version switch always applies the first poll for
    // the newly-selected pin instead of comparing against the prior pin's
    // fingerprint (which could coincidentally match and skip a real change).
    revisionsFingerprintRef.current = null;
    remotePushSeenRevisionRef.current = {};
    remotePushHasBaselineRef.current = false;
    setRemotePushByTemplateId({});
  }, [
    selectedRogerDocFormType,
    selectedVersion?.name,
    selectedVersion?.version,
  ]);

  // Acknowledge section/modal dots when a template becomes the open editor
  // target; keep the header pill until a local save (EMR-277).
  useEffect(() => {
    const id = editorTarget?.editingTemplate?.id ?? null;
    if (!id) return;
    setRemotePushByTemplateId(prev => acknowledgeRemotePush(prev, id));
  }, [editorTarget?.editingTemplate?.id]);

  useEffect(() => {
    if (!selectedVersion || !selectedRogerDocFormType) return;

    let cancelled = false;

    const poll = async () => {
      let revisions: BespokeTemplateRevision[];
      try {
        const res =
          await listBespokeTemplateRevisionsBespokeTemplatesRevisionsGet({
            query: {
              form_type: selectedRogerDocFormType,
              schema_version_name: selectedVersion.name,
              form_schema_version: selectedVersion.version,
            },
            throwOnError: true,
          });
        revisions = (res?.data as BespokeTemplateRevision[] | undefined) ?? [];
      } catch (err) {
        // Silent by design: this is a background poll, not a user action —
        // a transient failure here shouldn't interrupt editing with a toast.
        console.error('Failed to poll bespoke template revisions:', err);
        return;
      }
      if (cancelled) return;

      // Remote-push detection runs on every tick, independent of the
      // fingerprint gate below — it's a cheap in-memory diff, and gating it
      // on the same fingerprint would miss a push that lands between two
      // otherwise-identical polls (e.g. a push immediately reverted).
      const { nextById, pushed } = diffRevisionsForRemotePush({
        revisions,
        previousById: remotePushSeenRevisionRef.current,
        ownSaveTemplateIds: ownSaveTemplateIdsRef.current,
        hasBaseline: remotePushHasBaselineRef.current,
      });
      remotePushSeenRevisionRef.current = nextById;
      remotePushHasBaselineRef.current = true;
      ownSaveTemplateIdsRef.current.clear();
      setRemotePushByTemplateId(prev =>
        applyRemotePushDelta(prev, {
          pushed,
          presentTemplateIds: new Set(revisions.map(r => r.id)),
        })
      );

      const fingerprint = bespokeTemplateRevisionsFingerprint(revisions);
      if (fingerprint === revisionsFingerprintRef.current) return;

      let list: BespokeTemplateResponse[];
      try {
        const res = await listBespokeTemplatesBespokeTemplatesGet({
          query: {
            form_type: selectedRogerDocFormType,
            schema_version_name: selectedVersion.name,
            form_schema_version: selectedVersion.version,
          },
          throwOnError: true,
        });
        list = (res?.data as BespokeTemplateResponse[] | undefined) ?? [];
        setTemplates(prev => upsertBespokeTemplatesLocal(prev, list));
        setTemplatesLoadFailed(false);
      } catch (err) {
        // Same silent policy as revisions poll: do not toast or wipe the catalog.
        console.error(
          'Failed to refresh bespoke templates after revisions change:',
          err
        );
        return;
      }
      if (cancelled) return;

      const key = selectedSectionName
        ? sectionEditorDraftKey(
            selectedRogerDocFormType,
            selectedVersion.name,
            selectedSectionName
          )
        : null;
      const draft = key ? useStore.getState().editorDraftByKey[key] : undefined;

      const refreshPlan = planRevisionsPollFingerprintRefresh({
        revisions,
        previousFingerprint: revisionsFingerprintRef.current,
        editingTemplateId: draft?.editingTemplateId,
        isDraftDirty: draft ? isSectionEditorDraftDirty(draft) : false,
        refreshedTemplates: list,
      });

      if (refreshPlan.kind === 'fingerprint_unchanged') return;
      if (refreshPlan.kind === 'defer_dirty_draft') return;

      if (refreshPlan.templateToSync && key) {
        const seed = buildSectionEditorSeed(
          refreshPlan.templateToSync.section_name,
          refreshPlan.templateToSync
        );
        if (seed) {
          setSectionEditorDraft(key, seed);
          setEditorTarget({
            editingTemplate: refreshPlan.templateToSync,
            draft: null,
          });
        }
      }

      revisionsFingerprintRef.current = refreshPlan.nextFingerprint;
    };

    const runPollIfVisible = () => {
      if (document.hidden) return;
      void poll();
    };

    const intervalId = setInterval(runPollIfVisible, 3000);
    runPollIfVisible();

    const onVisibilityChange = () => {
      if (!document.hidden && !cancelled) {
        void poll();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [
    selectedRogerDocFormType,
    selectedVersion,
    selectedSectionName,
    buildSectionEditorSeed,
    setSectionEditorDraft,
  ]);

  const openEditorForTemplate = useCallback(
    (template: BespokeTemplateResponse) => {
      if (!selectedVersion || !selectedRogerDocFormType) return;
      setLayoutJsonOpen(false);
      setSelectedSectionName(template.section_name);
      const active =
        enabledTemplateBySection.get(template.section_name) ?? null;
      updateUrlSelection({
        section: template.section_name,
        layoutTemplate: layoutTemplateQueryValue(template, active),
      });
      setEditorTarget({ editingTemplate: template, draft: null });
      const seed = buildSectionEditorSeed(template.section_name, template);
      if (seed) {
        setSectionEditorDraft(
          sectionEditorDraftKey(
            selectedRogerDocFormType,
            selectedVersion.name,
            template.section_name
          ),
          seed
        );
      }
    },
    [
      selectedVersion,
      selectedRogerDocFormType,
      enabledTemplateBySection,
      updateUrlSelection,
      buildSectionEditorSeed,
      setSectionEditorDraft,
    ]
  );

  const {
    matchingCopyOpen,
    matchingCopySectionName,
    matchingCopyScanning,
    matchingCopyMatches,
    matchingCopyError,
    matchingCopySelectedTemplateId,
    setMatchingCopySelectedTemplateId,
    matchingCopyApplying,
    openMatchingCopyDialog,
    closeMatchingCopyDialog,
    handleApplyMatchingCopy,
  } = useMatchingLayoutCopyDialog({
    selectedVersion,
    selectedRogerDocFormType,
    existingTemplateBySection,
    toast,
    onTemplateSaved: upsertTemplateLocal,
  });

  const [sectionPortOpen, setSectionPortOpen] = useState(false);
  const [sectionPortSource, setSectionPortSource] = useState<{
    sectionName: string;
    layout: LayoutNode[];
    questions: FormTreeQuestion[];
    sourcePin: FormSchemaVersionPin;
  } | null>(null);

  const openCopyToSimilarSections = useCallback(
    (sectionName: string) => {
      // Guard on `selectedPin`, not just `selectedVersion` — the dialog
      // itself only mounts when `selectedPin` is set (it also needs
      // `selectedRogerDocFormType` to resolve), so checking `selectedVersion`
      // alone can pass here while the dialog silently fails to open.
      if (!selectedVersion || !selectedPin) {
        toast.error('Pick a version', 'Select a schema version first.');
        return;
      }
      const template = enabledTemplateBySection.get(sectionName);
      const sectionQuestions = formTree?.[sectionName] ?? [];
      const layoutState = getSectionLayoutState(template, sectionQuestions);
      if (layoutState.kind !== 'valid') {
        toast.error(
          'No layout to copy',
          'This section needs a valid, enabled layout before porting to other forms.'
        );
        return;
      }
      // Shares isEligibleForSectionPortCopy with the nav's menu-item
      // `disabled` check (FormSchemaSectionNav.tsx) so the two guards can't
      // drift apart if a second entry point into this handler is ever added.
      const dirty = selectedRogerDocFormType
        ? isSectionEditorDraftDirty(
            editorDraftByKey[
              sectionEditorDraftKey(
                selectedRogerDocFormType,
                selectedVersion.name,
                sectionName
              )
            ]
          )
        : false;
      const hasQuestions = sectionQuestions.length > 0;
      if (!isEligibleForSectionPortCopy({ layoutState, dirty, hasQuestions })) {
        toast.error(
          'Section not ready to copy',
          'Save this section and make sure its questions have loaded before porting to other forms.'
        );
        return;
      }
      setSectionPortSource({
        sectionName,
        layout: layoutState.template.layout_json as LayoutNode[],
        questions: sectionQuestions,
        sourcePin: selectedPin,
      });
      setSectionPortOpen(true);
      if (selectedSectionName !== sectionName) {
        handleSectionClick(sectionName);
      }
    },
    [
      editorDraftByKey,
      enabledTemplateBySection,
      formTree,
      handleSectionClick,
      selectedPin,
      selectedRogerDocFormType,
      selectedSectionName,
      selectedVersion,
      toast,
    ]
  );

  const templatesBySection = useMemo(() => {
    const map = new Map<string, BespokeTemplateResponse[]>();
    for (const template of visitTypeTemplates) {
      const existing = map.get(template.section_name) ?? [];
      existing.push(template);
      map.set(template.section_name, existing);
    }
    for (const rows of map.values()) {
      rows.sort((a, b) => {
        const d = bespokeStatusTier(a.status) - bespokeStatusTier(b.status);
        if (d !== 0) return d;
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
    }
    return map;
  }, [visitTypeTemplates]);

  const handleCopyTemplateLayoutJson = useCallback(
    async (template: BespokeTemplateResponse | null) => {
      if (!template?.layout_json) {
        toast.error('Nothing to copy', 'This template has no assigned layout.');
        return;
      }
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(template.layout_json, null, 2)
        );
        toast.success('Copied', 'Roger Layout Schema copied to clipboard.');
      } catch {
        toast.error('Clipboard failed', 'Could not write to clipboard.');
      }
    },
    [toast]
  );

  const handleCopySectionLayoutJson = useCallback(
    async (layoutState: SectionLayoutState) => {
      await handleCopyTemplateLayoutJson(
        layoutState.kind === 'missing' ? null : layoutState.template
      );
    },
    [handleCopyTemplateLayoutJson]
  );

  const handleAssignSectionLayoutJsonFromClipboard = useCallback(
    async (sectionName: string, questions: FormTreeQuestion[]) => {
      if (!selectedVersion || !selectedRogerDocFormType) {
        toast.error('Pick a version', 'Select a schema version first.');
        return;
      }

      let clipboardText = '';
      try {
        clipboardText = await navigator.clipboard.readText();
      } catch {
        toast.error('Clipboard failed', 'Could not read from clipboard.');
        return;
      }

      const validation = validateLayoutJsonForSection(clipboardText, questions);
      if (!validation.ok) {
        toast.error('Invalid Roger Layout Schema', validation.error);
        return;
      }

      const key = sectionEditorDraftKey(
        selectedRogerDocFormType,
        selectedVersion.name,
        sectionName
      );
      const layoutJsonText = JSON.stringify(validation.layout, null, 2);
      const displayName = formatSectionDropdownLabel(
        sectionName,
        formTreeLabelByCategory
      );
      // Prefer Live/Paused primary; otherwise update any existing template for
      // the section (Standby/Draft) so paste does not create a duplicate Live.
      const templateToEdit =
        enabledTemplateBySection.get(sectionName) ??
        existingTemplateBySection.get(sectionName) ??
        null;

      handleSectionClick(sectionName);

      const existingDraft = useStore.getState().editorDraftByKey[key];
      if (!existingDraft) {
        const seed = buildSectionEditorSeed(sectionName, templateToEdit);
        if (!seed) return;
        if (templateToEdit) {
          setEditorTarget({ editingTemplate: templateToEdit, draft: null });
        } else {
          setEditorTarget({
            editingTemplate: null,
            draft: {
              schemaVersionName: selectedVersion.name,
              schemaVersionValue: selectedVersion.version,
              rogerDocFormType: selectedRogerDocFormType,
              sectionName,
              name: seed.name,
              description: seed.description,
              layoutJsonText: seed.layoutJsonText,
              layoutStatus: seed.status,
            },
          });
        }
        hydrateSectionEditorDraft({ key, seed });
      } else if (
        templateToEdit &&
        existingDraft.editingTemplateId == null &&
        !isSectionEditorDraftDirty(existingDraft)
      ) {
        const seed = buildSectionEditorSeed(sectionName, templateToEdit);
        if (seed) {
          setSectionEditorDraft(key, seed);
          setEditorTarget({ editingTemplate: templateToEdit, draft: null });
        }
      }

      setSectionEditorField(key, { layoutJsonText });
      toast.success(
        'Layout pasted',
        `${displayName} updated locally. Save when ready.`
      );
    },
    [
      buildSectionEditorSeed,
      enabledTemplateBySection,
      existingTemplateBySection,
      formTreeLabelByCategory,
      handleSectionClick,
      hydrateSectionEditorDraft,
      selectedRogerDocFormType,
      selectedVersion,
      setSectionEditorDraft,
      setSectionEditorField,
      toast,
    ]
  );

  const handlePasteTemplateLayoutFromClipboard = useCallback(
    async (
      template: BespokeTemplateResponse,
      questions: FormTreeQuestion[]
    ) => {
      if (!selectedVersion || !selectedRogerDocFormType) {
        toast.error('Pick a version', 'Select a schema version first.');
        return;
      }

      let clipboardText = '';
      try {
        clipboardText = await navigator.clipboard.readText();
      } catch {
        toast.error('Clipboard failed', 'Could not read from clipboard.');
        return;
      }

      const validation = validateLayoutJsonForSection(clipboardText, questions);
      if (!validation.ok) {
        toast.error('Invalid Roger Layout Schema', validation.error);
        return;
      }

      const sectionName = template.section_name;
      const key = sectionEditorDraftKey(
        selectedRogerDocFormType,
        selectedVersion.name,
        sectionName
      );
      const layoutJsonText = JSON.stringify(validation.layout, null, 2);
      const displayName = formatSectionDropdownLabel(
        sectionName,
        formTreeLabelByCategory
      );

      openEditorForTemplate(template);

      setSectionEditorField(key, { layoutJsonText });
      toast.success(
        'Layout pasted',
        `${displayName} (${template.name}) updated locally. Save when ready.`
      );
    },
    [
      formTreeLabelByCategory,
      openEditorForTemplate,
      selectedRogerDocFormType,
      selectedVersion,
      setSectionEditorField,
      toast,
    ]
  );

  const validationSeedVersionsByFormType = useMemo(() => {
    if (!selectedRogerDocFormType || versions.length === 0) return undefined;
    return new Map([[selectedRogerDocFormType, versions]]);
  }, [selectedRogerDocFormType, versions]);

  const handleGenerateSectionLayout = useCallback(
    (
      sectionName: string,
      questions: FormTreeQuestion[],
      prompt = DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT
    ) => {
      if (!selectedVersion || !selectedRogerDocFormType) {
        toast.error('Pick a version', 'Select a schema version first.');
        return;
      }
      if (!selectedVersion.version) {
        toast.error(
          'Missing schema version',
          'The selected schema is missing a version identifier.'
        );
        return;
      }
      const trimmed = prompt.trim();
      if (!trimmed) {
        toast.error('Prompt required', 'Enter a prompt to generate a layout.');
        return;
      }
      startFormSchemaLayoutGeneration({
        formType: selectedRogerDocFormType,
        schemaVersionName: selectedVersion.name,
        formSchemaVersion: selectedVersion.version,
        sectionName,
        questions,
        prompt: trimmed,
        visitTypeSummary:
          selectedCatalogEntry?.display_label ?? selectedRogerDocFormType,
        canonicalVisitTypeId: canonicalVisitForApi,
        isOasis: selectedCatalogEntry?.is_oasis,
        usedDefaultPrompt: trimmed === DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT,
        onTemplateSaved: upsertTemplateLocal,
        toast,
      });
    },
    [
      canonicalVisitForApi,
      selectedCatalogEntry,
      selectedRogerDocFormType,
      selectedVersion,
      startFormSchemaLayoutGeneration,
      toast,
      upsertTemplateLocal,
    ]
  );

  const handleCreateSectionLayout = useCallback(
    (sectionName: string) => {
      if (!selectedVersion || !selectedRogerDocFormType) {
        toast.error('Pick a version', 'Select a schema version first.');
        return;
      }
      setSelectedSectionName(sectionName);
      updateUrlSelection({ section: sectionName, layoutTemplate: null });
      setEditorTarget({
        editingTemplate: null,
        draft: {
          schemaVersionName: selectedVersion.name,
          schemaVersionValue: selectedVersion.version,
          rogerDocFormType: selectedRogerDocFormType,
          sectionName,
          name: defaultBlankSectionLayoutName(
            formatSectionDropdownLabel(sectionName, formTreeLabelByCategory)
          ),
          description: '',
          layoutJsonText: '[]',
          layoutStatus: 'live',
        },
      });
      const seed = buildSectionEditorSeed(sectionName, null);
      if (seed) {
        setSectionEditorDraft(
          sectionEditorDraftKey(
            selectedRogerDocFormType,
            selectedVersion.name,
            sectionName
          ),
          seed
        );
      }
    },
    [
      buildSectionEditorSeed,
      formTreeLabelByCategory,
      selectedRogerDocFormType,
      selectedVersion,
      setSectionEditorDraft,
      toast,
      updateUrlSelection,
    ]
  );

  const selectedSectionQuestions =
    selectedSectionName && formTree ? formTree[selectedSectionName] : null;
  const selectedSectionEditorKey =
    selectedVersion && selectedSectionName && selectedRogerDocFormType
      ? sectionEditorDraftKey(
          selectedRogerDocFormType,
          selectedVersion.name,
          selectedSectionName
        )
      : null;
  const selectedSectionEditorDraft = selectedSectionEditorKey
    ? editorDraftByKey[selectedSectionEditorKey]
    : undefined;
  const editorTargetTemplateId = editorTarget?.editingTemplate?.id ?? null;
  /** Only render once section, target, and store draft all agree on the same form. */
  const editorMatchesSection =
    !!editorTarget &&
    !!selectedSectionName &&
    (editorTarget.editingTemplate?.section_name === selectedSectionName ||
      editorTarget.draft?.sectionName === selectedSectionName) &&
    selectedSectionEditorDraft != null &&
    selectedSectionEditorDraft.editingTemplateId === editorTargetTemplateId;

  const versionsSectionQuestions =
    versionsSection && formTree ? formTree[versionsSection] : null;
  const versionsSectionTemplates = versionsSection
    ? (templatesBySection.get(versionsSection) ?? [])
    : [];
  const customGenerateInProgress =
    customGenerateDialog != null &&
    selectedVersion != null &&
    sectionGenerationStatusByKey[
      layoutGenerationKey(
        selectedVersion.name,
        customGenerateDialog.sectionName
      )
    ]?.state === 'loading';

  // -- permission guard --
  if (!hasPermission('emr:manage')) {
    return (
      <Card className='max-w-md mx-auto mt-8'>
        <CardHeader>
          <CardTitle className='flex items-center gap-2 text-destructive'>
            <ShieldX className='h-5 w-5' />
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className='text-muted-foreground'>
            You don&apos;t have permission to access Form Schema management.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>Forms Manager</h1>
          <p className='text-muted-foreground'>
            Browse schema versions and preview, edit, and validate each
            section&apos;s form layout.
          </p>
        </div>
        <div className='flex flex-wrap gap-2 shrink-0 self-start'>
          <Button
            type='button'
            variant='outline'
            className='shrink-0'
            disabled={catalogLoading || formTypeCatalog.length === 0}
            onClick={() => setImportModalOpen(true)}
          >
            <Upload className='h-4 w-4 mr-2' aria-hidden />
            Import…
          </Button>
          <Button
            type='button'
            variant='outline'
            className='shrink-0'
            disabled={catalogLoading || formTypeCatalog.length === 0}
            onClick={() => setExportModalOpen(true)}
          >
            <Download className='h-4 w-4 mr-2' aria-hidden />
            Export…
          </Button>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type='button'
                variant='outline'
                className='h-8 w-8 shrink-0 p-0 [&_svg]:h-4 [&_svg]:w-4'
                disabled={catalogLoading || formTypeCatalog.length === 0}
                aria-label='Bulk rendering for entire catalog'
              >
                <MoreHorizontal className='h-4 w-4' aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='end'
              className='w-[min(100vw-2rem,22rem)]'
            >
              <DropdownMenuItem onClick={() => setValidationResultsOpen(true)}>
                <ClipboardCheck className='h-3.5 w-3.5 mr-2' aria-hidden />
                Validation Results…
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={
                  catalogLoading ||
                  countBespokeBulkTargets(templates, 'pause', null) === 0
                }
                onClick={() =>
                  openBulkBespokeDialog(
                    'pause',
                    'All bespoke templates in this environment.',
                    null
                  )
                }
              >
                <Pause className='h-3.5 w-3.5 mr-2' aria-hidden />
                Pause all Live (entire catalog)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={
                  catalogLoading ||
                  countBespokeBulkTargets(templates, 'resume', null) === 0
                }
                onClick={() =>
                  openBulkBespokeDialog(
                    'resume',
                    'All bespoke templates in this environment.',
                    null
                  )
                }
              >
                <Play className='h-3.5 w-3.5 mr-2' aria-hidden />
                Resume all Paused (entire catalog)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Section 1: Schema Version Browser */}
      <div className='space-y-4'>
        <div className='max-w-xs flex-1 min-w-[12rem]'>
          <label className='mb-1 block text-sm font-medium'>Form Type</label>
          <div className='flex items-center gap-2'>
            <div className='min-w-0 flex-1'>
              <Select
                value={selectedRogerDocFormType ?? ''}
                onValueChange={v => {
                  const nextFormType = v as RogerDocFormType;
                  setSelectedRogerDocFormType(nextFormType);
                  setVersions([]);
                  setVersionsError(null);
                  setVersionsLoading(true);
                  setSelectedVersion(null);
                  setSelectedSectionName(null);
                  setFormTree(null);
                  setFormTreeSectionOrder(null);
                  setTreeLoading(false);
                  setEditorTarget(null);
                  setVersionsSection(null);
                  initialVersionUrlConsumedRef.current = true;
                  initialUrlStateRef.current = {
                    ...initialUrlStateRef.current,
                    formType: nextFormType,
                    schemaName: null,
                    schemaVersion: null,
                    section: null,
                    layoutTemplate: null,
                  };
                  updateUrlSelection({
                    formType: nextFormType,
                    schemaName: null,
                    schemaVersion: null,
                    section: null,
                    layoutTemplate: null,
                  });
                }}
                disabled={catalogLoading || formTypeCatalog.length === 0}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={
                      catalogLoading ? 'Loading form types…' : 'No form types'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {sortFormTypeCatalogByFamily(formTypeCatalog).map(row => (
                    <SelectItem key={row.form_type} value={row.form_type}>
                      {row.display_label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  variant='outline'
                  className='h-8 w-8 shrink-0 p-0 [&_svg]:h-4 [&_svg]:w-4'
                  disabled={
                    catalogLoading ||
                    !selectedRogerDocFormType ||
                    pinsForCurrentFormType.length === 0 ||
                    versionsLoading
                  }
                  aria-label={`Bulk rendering for ${selectedCatalogEntry?.display_label ?? 'form type'}`}
                >
                  <MoreHorizontal className='h-4 w-4' aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align='start'
                className='w-[min(100vw-2rem,22rem)]'
              >
                <DropdownMenuItem
                  disabled={
                    countBespokeBulkTargets(
                      templates,
                      'pause',
                      pinsForCurrentFormType
                    ) === 0
                  }
                  onClick={() =>
                    openBulkBespokeDialog(
                      'pause',
                      `All versions of ${selectedCatalogEntry?.display_label ?? String(selectedRogerDocFormType)} listed below (${pinsForCurrentFormType.length} schema version${pinsForCurrentFormType.length === 1 ? '' : 's'}).`,
                      pinsForCurrentFormType
                    )
                  }
                >
                  <Pause className='h-3.5 w-3.5 mr-2' aria-hidden />
                  Pause all Live for this form type
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={
                    countBespokeBulkTargets(
                      templates,
                      'resume',
                      pinsForCurrentFormType
                    ) === 0
                  }
                  onClick={() =>
                    openBulkBespokeDialog(
                      'resume',
                      `All versions of ${selectedCatalogEntry?.display_label ?? String(selectedRogerDocFormType)} listed below (${pinsForCurrentFormType.length} schema version${pinsForCurrentFormType.length === 1 ? '' : 's'}).`,
                      pinsForCurrentFormType
                    )
                  }
                >
                  <Play className='h-3.5 w-3.5 mr-2' aria-hidden />
                  Resume all Paused for this form type
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className='rounded-md border'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className='py-1.5'>Name</TableHead>
                <TableHead className='py-1.5'>Effective From</TableHead>
                <TableHead className='py-1.5'>Effective Until</TableHead>
                <TableHead className='py-1.5 text-right'>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versionsLoading ? (
                <TableRow>
                  <TableCell colSpan={4} className='text-center py-8'>
                    <Loader2 className='h-5 w-5 animate-spin mx-auto' />
                  </TableCell>
                </TableRow>
              ) : versionsError ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className='text-center py-8 text-destructive'
                  >
                    Could not load schema versions for this form type.
                    <span className='mt-1 block text-sm text-muted-foreground'>
                      {versionsError}
                    </span>
                  </TableCell>
                </TableRow>
              ) : versions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className='text-center py-8 text-muted-foreground'
                  >
                    No schema versions found for this visit type.
                  </TableCell>
                </TableRow>
              ) : (
                versions.map(v => {
                  const active = isEffective(v);
                  const versionKey = `${v.name}::${v.version ?? ''}`;
                  const selectedKey = selectedVersion
                    ? `${selectedVersion.name}::${selectedVersion.version ?? ''}`
                    : null;
                  const selected = selectedKey === versionKey;
                  const versionBulkPin = selectedRogerDocFormType
                    ? pinFromFormSchemaVersion(
                        selectedRogerDocFormType as RogerDocFormType,
                        v
                      )
                    : null;
                  const versionBulkPins = versionBulkPin
                    ? [versionBulkPin]
                    : [];
                  return (
                    <TableRow
                      key={versionKey}
                      className={`cursor-pointer ${selected ? 'bg-muted' : 'hover:bg-muted/50'}`}
                      onClick={() => handleVersionClick(v)}
                    >
                      <TableCell className='font-medium py-1.5'>
                        {v.name}
                        {active && (
                          <Badge className='ml-2 text-xs' variant='default'>
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className='py-1.5'>
                        {v.effective_from
                          ? formatDateForDisplay(v.effective_from) || '—'
                          : '—'}
                      </TableCell>
                      <TableCell className='py-1.5'>
                        {v.effective_until
                          ? formatDateForDisplay(v.effective_until) || '—'
                          : '—'}
                      </TableCell>
                      <TableCell className='text-right py-1.5'>
                        <div
                          className='flex justify-end'
                          onClick={event => event.stopPropagation()}
                        >
                          <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type='button'
                                variant='ghost'
                                size='sm'
                                className='h-8 w-8 p-0'
                                aria-label='Schema version actions'
                                onClick={() => {
                                  if (!selected) handleVersionClick(v);
                                }}
                              >
                                <MoreHorizontal className='h-4 w-4' />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align='end'>
                              <DropdownMenuItem
                                onClick={() =>
                                  void openBulkMatchingCopyForSchemaVersion(v)
                                }
                              >
                                <SquareStack
                                  className='h-3.5 w-3.5 mr-2'
                                  aria-hidden
                                />
                                Copy matching layouts…
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                disabled={
                                  countBespokeBulkTargets(
                                    templates,
                                    'pause',
                                    versionBulkPins
                                  ) === 0
                                }
                                onClick={() =>
                                  openBulkBespokeDialog(
                                    'pause',
                                    `Schema version "${v.name}" (${v.version ?? 'unknown version'}).`,
                                    versionBulkPins
                                  )
                                }
                              >
                                <Pause className='h-3.5 w-3.5 mr-2' />
                                Pause Live for this version
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={
                                  countBespokeBulkTargets(
                                    templates,
                                    'resume',
                                    versionBulkPins
                                  ) === 0
                                }
                                onClick={() =>
                                  openBulkBespokeDialog(
                                    'resume',
                                    `Schema version "${v.name}" (${v.version ?? 'unknown version'}).`,
                                    versionBulkPins
                                  )
                                }
                              >
                                <Play className='h-3.5 w-3.5 mr-2' />
                                Resume Paused for this version
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {selectedVersion &&
          selectedVersion.form_type === selectedRogerDocFormType && (
            <div className='pt-4'>
              {treeLoading ? (
                <div className='flex items-center justify-center py-8'>
                  <Loader2 className='h-5 w-5 animate-spin' />
                </div>
              ) : !formTree ? (
                <p className='text-muted-foreground text-center py-8'>
                  Failed to load form tree.
                </p>
              ) : orderedFormTreeSections.length === 0 ? (
                <p className='text-muted-foreground text-center py-8'>
                  This schema version has no sections.
                </p>
              ) : (
                <div className='flex min-h-[32rem] gap-3'>
                  <FormSchemaSectionNav
                    sections={orderedFormTreeSections}
                    labelFor={section =>
                      formatSectionDropdownLabel(
                        section,
                        formTreeLabelByCategory
                      )
                    }
                    selectedSection={selectedSectionName}
                    onSelect={handleSectionClick}
                    getLayoutState={section =>
                      getSectionLayoutState(
                        enabledTemplateBySection.get(section),
                        formTree[section] ?? []
                      )
                    }
                    getGenerationStatus={section =>
                      sectionGenerationStatusByKey[
                        layoutGenerationKey(selectedVersion.name, section)
                      ]
                    }
                    getChatPending={section =>
                      selectedRogerDocFormType
                        ? (editorDraftByKey[
                            sectionEditorDraftKey(
                              selectedRogerDocFormType,
                              selectedVersion.name,
                              section
                            )
                          ]?.chatPending ?? false)
                        : false
                    }
                    getIsDirty={section =>
                      selectedRogerDocFormType
                        ? isSectionEditorDraftDirty(
                            editorDraftByKey[
                              sectionEditorDraftKey(
                                selectedRogerDocFormType,
                                selectedVersion.name,
                                section
                              )
                            ]
                          )
                        : false
                    }
                    getHasQuestions={section =>
                      (formTree?.[section]?.length ?? 0) > 0
                    }
                    getHasRemotePush={section =>
                      latestUnacknowledgedRemotePush(
                        remotePushByTemplateId,
                        (templatesBySection.get(section) ?? []).map(t => t.id)
                      ) != null
                    }
                    getRemotePushTooltip={section => {
                      const latest = latestUnacknowledgedRemotePush(
                        remotePushByTemplateId,
                        (templatesBySection.get(section) ?? []).map(t => t.id)
                      );
                      return latest
                        ? `New layout pushed · ${formatRemotePushTimeLabel(latest.updatedAt)}`
                        : undefined;
                    }}
                    onGenerate={section =>
                      void handleGenerateSectionLayout(
                        section,
                        formTree[section] ?? []
                      )
                    }
                    onGenerateCustom={section => {
                      setCustomGeneratePrompt(
                        DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT
                      );
                      setCustomGenerateDialog({
                        sectionName: section,
                        displayName: formatSectionDropdownLabel(
                          section,
                          formTreeLabelByCategory
                        ),
                        questions: formTree[section] ?? [],
                      });
                    }}
                    onAddFromClipboard={section =>
                      void handleAssignSectionLayoutJsonFromClipboard(
                        section,
                        formTree[section] ?? []
                      )
                    }
                    onCopyLayout={section =>
                      void handleCopySectionLayoutJson(
                        getSectionLayoutState(
                          enabledTemplateBySection.get(section),
                          formTree[section] ?? []
                        )
                      )
                    }
                    onCopyFromMatching={section =>
                      openMatchingCopyDialog(section, formTree[section] ?? [])
                    }
                    onCopyToSimilarSections={section =>
                      openCopyToSimilarSections(section)
                    }
                    onViewQuestions={section =>
                      setQuestionDialog({
                        sectionName: section,
                        questions: formTree[section] ?? [],
                      })
                    }
                    onOpenVersions={section => {
                      handleSectionClick(section);
                      setVersionsSection(section);
                    }}
                    onOpenLayoutJson={section => {
                      handleSectionClick(section);
                      setLayoutJsonOpen(true);
                    }}
                  />
                  <div className='min-w-0 flex-1 pl-2'>
                    {selectedSectionName &&
                    selectedSectionQuestions &&
                    editorTarget &&
                    editorMatchesSection ? (
                      <SectionEditorPanel
                        key={`${selectedVersion.name}::${selectedSectionName}`}
                        editingTemplate={editorTarget.editingTemplate}
                        initialDraft={editorTarget.draft}
                        onSaveJson={handlePersistVibecoderJson}
                        saving={saving}
                        formTypeCatalog={formTypeCatalog}
                        catalogLoading={catalogLoading}
                        selectedRogerDocFormType={selectedRogerDocFormType}
                        activeTemplateId={
                          selectedSectionName
                            ? (enabledTemplateBySection.get(selectedSectionName)
                                ?.id ?? null)
                            : null
                        }
                        remotePush={
                          editorTarget.editingTemplate
                            ? (remotePushByTemplateId[
                                editorTarget.editingTemplate.id
                              ] ?? null)
                            : null
                        }
                        layoutJsonOpen={layoutJsonOpen}
                        onLayoutJsonOpenChange={setLayoutJsonOpen}
                      />
                    ) : (
                      <div className='flex min-h-[24rem] items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground'>
                        Select a section to edit its layout.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
      </div>

      <Dialog
        open={bulkBespokeDialog != null}
        onOpenChange={open => {
          if (!open && !bulkBespokeBusy) {
            setBulkBespokeDialog(null);
          }
        }}
      >
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>
              {bulkBespokeDialog?.direction === 'resume'
                ? 'Resume paused rendering'
                : 'Pause live rendering'}
            </DialogTitle>
            <DialogDescription asChild>
              <div className='space-y-3 text-muted-foreground text-sm'>
                <p>{bulkBespokeDialog?.scopeSummary}</p>
                <p className='text-foreground font-medium'>
                  {bulkBespokeDialog?.affectedCount ?? 0} template
                  {(bulkBespokeDialog?.affectedCount ?? 0) === 1
                    ? ''
                    : 's'}{' '}
                  will be updated (
                  {bulkBespokeDialog?.direction === 'resume'
                    ? 'Paused → Live'
                    : 'Live → Paused'}
                  ).
                </p>
                <p>
                  Templates with <span className='font-medium'>Standby</span>{' '}
                  status are not changed.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='gap-2 sm:gap-0'>
            <Button
              type='button'
              variant='outline'
              disabled={bulkBespokeBusy}
              onClick={() => setBulkBespokeDialog(null)}
            >
              Cancel
            </Button>
            <Button
              type='button'
              disabled={
                bulkBespokeBusy || (bulkBespokeDialog?.affectedCount ?? 0) === 0
              }
              onClick={() => void runBulkBespokeConfirmed()}
            >
              {bulkBespokeBusy ? (
                <>
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' aria-hidden />
                  Updating…
                </>
              ) : bulkBespokeDialog?.direction === 'resume' ? (
                'Resume'
              ) : (
                'Pause'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={matchingCopyOpen}
        onOpenChange={open => {
          if (!open) closeMatchingCopyDialog();
        }}
      >
        <DialogContent className='max-h-[85vh] max-w-lg overflow-hidden flex flex-col'>
          <DialogHeader>
            <DialogTitle>Copy from matching section</DialogTitle>
            <DialogDescription>
              Destination:{' '}
              <span className='font-medium text-foreground'>
                {matchingCopySectionName
                  ? formatSectionDropdownLabel(matchingCopySectionName)
                  : '—'}
              </span>{' '}
              in {selectedVersion ? schemaVersionLabel(selectedVersion) : '—'}.
              Matches share the{' '}
              <span className='font-medium'>
                exact same answer-control QID set
              </span>{' '}
              as this section (layout coverage). Only{' '}
              <span className='font-medium'>live</span> layouts are listed—not
              standby or paused.
            </DialogDescription>
          </DialogHeader>
          <div className='min-h-0 flex-1 overflow-y-auto'>
            {matchingCopyScanning ? (
              <div className='flex items-center gap-2 py-6 text-sm text-muted-foreground'>
                <Loader2 className='h-4 w-4 shrink-0 animate-spin' />
                Looking up matching live templates…
              </div>
            ) : matchingCopyError ? (
              <p className='py-2 text-sm text-destructive'>
                {matchingCopyError}
              </p>
            ) : matchingCopyMatches.length === 0 ? (
              <p className='py-2 text-sm text-muted-foreground'>
                No other live layouts share this exact question set.
              </p>
            ) : (
              <RadioGroup
                value={matchingCopySelectedTemplateId}
                onValueChange={setMatchingCopySelectedTemplateId}
                className='gap-0'
              >
                <div className='space-y-2 pr-1'>
                  {matchingCopyMatches.map(match => (
                    <div
                      key={match.template.id}
                      className='flex gap-3 rounded-md border p-3 text-left'
                    >
                      <RadioGroupItem
                        value={match.template.id}
                        id={`matching-copy-${match.template.id}`}
                        className='mt-0.5 shrink-0'
                      />
                      <Label
                        htmlFor={`matching-copy-${match.template.id}`}
                        className='cursor-pointer text-xs font-normal leading-snug'
                      >
                        <span className='block text-foreground'>
                          {formatMatchingCopyAttribution(match)}
                        </span>
                      </Label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            )}
          </div>
          <p className='shrink-0 text-xs text-muted-foreground'>
            The saved template description will include the source shown above.
          </p>
          <DialogFooter className='shrink-0'>
            <Button
              type='button'
              variant='outline'
              onClick={closeMatchingCopyDialog}
              disabled={matchingCopyApplying}
            >
              Cancel
            </Button>
            <Button
              type='button'
              onClick={() => void handleApplyMatchingCopy()}
              disabled={
                matchingCopyApplying ||
                matchingCopyScanning ||
                matchingCopyMatches.length === 0 ||
                !matchingCopySelectedTemplateId
              }
            >
              {matchingCopyApplying && (
                <Loader2 className='h-3.5 w-3.5 mr-2 animate-spin' />
              )}
              Apply layout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {selectedVersion && sectionPortSource && (
        <SectionLayoutPortDialog
          open={sectionPortOpen}
          onClose={() => {
            setSectionPortOpen(false);
            setSectionPortSource(null);
          }}
          sourceLayout={sectionPortSource.layout}
          sourcePin={sectionPortSource.sourcePin}
          sourceSectionName={sectionPortSource.sectionName}
          sourceSectionSchema={sectionPortSource.questions as Question[]}
          formTypeCatalog={formTypeCatalog}
          toast={toast}
          onApplied={saved => upsertTemplatesLocal(saved)}
        />
      )}

      <FormBulkMatchingLayoutCopyDialog
        open={bulkMatchingCopyModalOpen}
        onOpenChange={setBulkMatchingCopyModalOpen}
        formTree={formTree}
        selectedVersion={selectedVersion}
        selectedRogerDocFormType={selectedRogerDocFormType}
        layoutTemplateBySection={enabledTemplateBySection}
        existingTemplateBySection={existingTemplateBySection}
        toast={toast}
        onTemplatesSaved={upsertTemplatesLocal}
      />

      <Dialog
        open={customGenerateDialog != null}
        onOpenChange={open => {
          if (!open) setCustomGenerateDialog(null);
        }}
      >
        <DialogContent className='max-w-2xl'>
          <DialogHeader>
            <DialogTitle>Generate with custom prompt</DialogTitle>
            <DialogDescription>
              {customGenerateDialog
                ? `Use a one-off prompt for ${customGenerateDialog.displayName}.`
                : 'Use a one-off prompt for this section.'}
            </DialogDescription>
          </DialogHeader>
          <div className='space-y-3'>
            <Textarea
              value={customGeneratePrompt}
              onChange={event => setCustomGeneratePrompt(event.target.value)}
              rows={8}
              className='min-h-40'
              placeholder='Describe the layout style, grouping, and priorities...'
            />
            <div className='flex justify-end gap-2'>
              <Button
                type='button'
                variant='outline'
                onClick={() => setCustomGenerateDialog(null)}
              >
                Cancel
              </Button>
              <Button
                type='button'
                disabled={
                  customGenerateInProgress ||
                  customGeneratePrompt.trim().length === 0
                }
                onClick={() => {
                  const target = customGenerateDialog;
                  const prompt = customGeneratePrompt.trim();
                  if (!target || !prompt) return;
                  setCustomGenerateDialog(null);
                  setCustomGeneratePrompt(
                    DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT
                  );
                  void handleGenerateSectionLayout(
                    target.sectionName,
                    target.questions,
                    prompt
                  );
                }}
              >
                {customGenerateInProgress && (
                  <Loader2 className='h-4 w-4 animate-spin mr-2' aria-hidden />
                )}
                Generate layout
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={importModalOpen} onOpenChange={setImportModalOpen}>
        <DialogContent className='max-w-3xl max-h-[min(92vh,56rem)] flex flex-col'>
          <DialogHeader>
            <DialogTitle>Import bespoke templates</DialogTitle>
            <DialogDescription>
              Choose a JSON bundle below. Templates apply to the shared
              environment-wide catalog (import always upserts by schema version,
              section, and template name).
            </DialogDescription>
          </DialogHeader>
          <input
            ref={importFileRef}
            type='file'
            accept='application/json,.json'
            className='hidden'
            onChange={e => void handleImportBundleFileChosen(e)}
          />

          <div className='shrink-0 flex flex-wrap items-center gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              disabled={
                catalogLoading ||
                importSubmitting ||
                formTypeCatalog.length === 0
              }
              onClick={() => importFileRef.current?.click()}
            >
              Choose JSON bundle…
            </Button>
            {importBundleSummary && (
              <span className='text-xs text-muted-foreground truncate max-w-[14rem] sm:max-w-xs'>
                {importBundleSummary.filename}
              </span>
            )}
          </div>

          {importParseError ? (
            <div className='rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
              {importParseError}
            </div>
          ) : null}

          {importBundleSummary ? (
            <div className='text-xs text-muted-foreground space-y-0.5'>
              <div>
                <span className='font-medium text-foreground'>Bundle</span>
                {importBundleSummary.exportScope != null &&
                importBundleSummary.exportScope !== ''
                  ? ` · scope: ${importBundleSummary.exportScope}`
                  : ''}
                {importBundleSummary.exportedAt
                  ? ` · exported ${importBundleSummary.exportedAt}`
                  : ''}
              </div>
              <div>
                <span className='font-medium text-foreground'>
                  {importBundleSummary.rowCount}
                </span>{' '}
                row(s) queued
              </div>
            </div>
          ) : (
            !importParseError && (
              <p className='text-sm text-muted-foreground'>
                Pick a bundle to preview grouped rows before importing.
              </p>
            )
          )}

          {importBundleSummary &&
          !importParseError &&
          importSourceRows.length > 0 ? (
            <div className='min-h-0 flex flex-1 flex-col gap-2'>
              {importPrepLoading ||
              formTypeCatalog.length === 0 ||
              importNestedGroups.length === 0 ? (
                <div
                  className='flex min-h-[12rem] max-h-[min(48vh,24rem)] flex-col items-center justify-center gap-2 rounded-md border border-border bg-muted/30 text-sm text-muted-foreground'
                  role='status'
                  aria-busy='true'
                  aria-live='polite'
                >
                  <div className='flex items-center gap-2'>
                    <Loader2
                      className='h-5 w-5 shrink-0 animate-spin'
                      aria-hidden
                    />
                    <span>Loading schema versions for grouping…</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className='flex flex-wrap gap-2'>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-8 px-2'
                      disabled={
                        importSubmitting || importAllLeafKeys.length === 0
                      }
                      onClick={() =>
                        setImportSelectedKeys(new Set(importAllLeafKeys))
                      }
                    >
                      Select all rows
                    </Button>
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      className='h-8 px-2'
                      disabled={importSubmitting}
                      onClick={() => setImportSelectedKeys(new Set())}
                    >
                      Clear all rows
                    </Button>
                  </div>
                  <SelectableNestedTable
                    className='min-h-[12rem] max-h-[min(48vh,24rem)] overflow-y-auto overflow-x-auto'
                    rows={importSelectableRows}
                    selectedKeys={importSelectedKeys}
                    setSelectedKeys={setImportSelectedKeys}
                    disabled={importSubmitting}
                  />
                </>
              )}
            </div>
          ) : null}

          <DialogFooter className='shrink-0'>
            <Button
              type='button'
              variant='outline'
              onClick={() => setImportModalOpen(false)}
              disabled={importSubmitting}
            >
              Cancel
            </Button>
            <Button
              type='button'
              onClick={() => void handleRunBespokeImport()}
              disabled={
                importPrepLoading ||
                importSubmitting ||
                importSelectedKeys.size === 0
              }
            >
              {importSubmitting && (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' aria-hidden />
              )}
              Import {importSelectedKeys.size} row
              {importSelectedKeys.size === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
        <DialogContent className='max-w-3xl max-h-[min(92vh,56rem)] flex flex-col'>
          <DialogHeader>
            <DialogTitle>Export bespoke templates</DialogTitle>
            <DialogDescription>
              Download a JSON bundle of the shared environment-wide bespoke
              catalog. Uncheck rows to exclude templates from the file.
            </DialogDescription>
          </DialogHeader>
          <div className='min-h-0 flex flex-1 flex-col gap-2'>
            {!exportPrepLoading &&
            exportCandidateRows.length === 0 &&
            exportModalOpen &&
            formTypeCatalog.length > 0 &&
            !catalogLoading ? (
              <p className='text-sm text-muted-foreground'>
                No bespoke templates match current RogerDoc catalog versions —
                nothing to export.
              </p>
            ) : null}
            <div className='flex flex-wrap gap-2'>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-8 px-2'
                disabled={
                  exportInProgress ||
                  exportPrepLoading ||
                  exportAllLeafKeys.length === 0
                }
                onClick={() =>
                  setExportSelectedLeafKeys(new Set(exportAllLeafKeys))
                }
              >
                Select all rows
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='sm'
                className='h-8 px-2'
                disabled={exportInProgress || exportPrepLoading}
                onClick={() => setExportSelectedLeafKeys(new Set())}
              >
                Clear all rows
              </Button>
            </div>
            {exportPrepLoading ? (
              <div
                className='flex min-h-[12rem] max-h-[min(48vh,24rem)] flex-col items-center justify-center gap-2 rounded-md border border-border bg-muted/30 text-sm text-muted-foreground'
                role='status'
                aria-busy='true'
                aria-live='polite'
              >
                <div className='flex items-center gap-2'>
                  <Loader2
                    className='h-5 w-5 shrink-0 animate-spin'
                    aria-hidden
                  />
                  <span>Loading bespoke templates</span>
                </div>
              </div>
            ) : (
              <SelectableNestedTable
                className='min-h-[12rem] max-h-[min(48vh,24rem)] overflow-y-auto overflow-x-auto'
                rows={exportSelectableUiRows}
                selectedKeys={exportSelectedLeafKeys}
                setSelectedKeys={setExportSelectedLeafKeys}
                disabled={
                  exportInProgress ||
                  catalogLoading ||
                  exportSelectableUiRows.length === 0
                }
              />
            )}
          </div>
          <DialogFooter className='shrink-0'>
            <Button
              type='button'
              variant='outline'
              onClick={() => setExportModalOpen(false)}
              disabled={exportInProgress}
            >
              Cancel
            </Button>
            <Button
              type='button'
              onClick={() => void handleRunBespokeExport()}
              disabled={
                exportInProgress ||
                exportSelectedLeafKeys.size === 0 ||
                exportPrepLoading
              }
            >
              {exportInProgress && (
                <Loader2 className='h-4 w-4 mr-2 animate-spin' aria-hidden />
              )}
              Download JSON
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuestionsDialog
        sectionName={questionDialog?.sectionName ?? null}
        questions={questionDialog?.questions ?? []}
        onClose={() => setQuestionDialog(null)}
      />

      {versionsSection && versionsSectionQuestions && (
        <SectionVersionsDialog
          open={versionsSection != null}
          onOpenChange={open => {
            if (!open) setVersionsSection(null);
          }}
          sectionDisplayName={formatSectionDropdownLabel(
            versionsSection,
            formTreeLabelByCategory
          )}
          templates={versionsSectionTemplates}
          questions={versionsSectionQuestions}
          busyTemplateId={templateStatusBusyId}
          getRemotePush={templateId =>
            unacknowledgedRemotePush(remotePushByTemplateId, templateId)
          }
          generationStatus={
            selectedVersion
              ? sectionGenerationStatusByKey[
                  layoutGenerationKey(selectedVersion.name, versionsSection)
                ]
              : undefined
          }
          onCreateLayout={() => {
            handleCreateSectionLayout(versionsSection);
            setVersionsSection(null);
          }}
          onGenerateLayout={() =>
            void handleGenerateSectionLayout(
              versionsSection,
              versionsSectionQuestions
            )
          }
          onGenerateLayoutWithCustomPrompt={() => {
            setCustomGeneratePrompt(DEFAULT_ROGER_LAYOUT_GENERATION_PROMPT);
            setCustomGenerateDialog({
              sectionName: versionsSection,
              displayName: formatSectionDropdownLabel(
                versionsSection,
                formTreeLabelByCategory
              ),
              questions: versionsSectionQuestions,
            });
            setVersionsSection(null);
          }}
          onAddLayoutFromClipboard={() =>
            void handleAssignSectionLayoutJsonFromClipboard(
              versionsSection,
              versionsSectionQuestions
            )
          }
          onEditTemplate={template => openEditorForTemplate(template)}
          onCopyTemplate={template =>
            void handleCopyTemplateLayoutJson(template)
          }
          onPasteTemplate={template =>
            void handlePasteTemplateLayoutFromClipboard(
              template,
              versionsSectionQuestions
            )
          }
          onSetTemplateStatus={(template, status) =>
            void handleSetBespokeTemplateStatus(template, status)
          }
          onDeleteTemplate={template => void handleDelete(template.id)}
          onEditTemplateDetails={handleSaveTemplateDetails}
        />
      )}

      <ValidationResultsDialog
        open={validationResultsOpen}
        onOpenChange={setValidationResultsOpen}
        templates={templates}
        formTypeCatalog={formTypeCatalog}
        seedVersionsByFormType={validationSeedVersionsByFormType}
      />
    </div>
  );
};

export default FormSchemas;
